import React, { useEffect, useRef, useState } from 'react';
import {
  Ruler,
  Settings,
  ChevronUp,
  ChevronDown,
  SlidersHorizontal,
  X,
  Layers,
  RotateCw,
  CheckCircle2,
  Sigma,
  Shirt,
  Fish,
  FoldVertical,
  ScissorsLineDashed,
  RulerDimensionLine,
  CircleDot,
  SigmaSquare,
  PanelTop,
  Gem,
  WalletCards,
  Info,
} from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

function IconTabRail({ tabs, activeTab, onTabChange, testId, dense = false }) {
  return (
    <div className={`${dense ? 'w-10 rounded-2xl p-1 space-y-1 border-slate-200 bg-white/90 shadow-sm' : 'w-11 rounded-xl p-1.5 space-y-1.5 bg-muted/20'} flex-shrink-0 border`} data-testid={testId}>
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.key;

        return (
          <button
            key={tab.key}
            type="button"
            title={tab.label}
            onClick={() => onTabChange(tab.key)}
            className={`w-full ${dense ? 'h-8 rounded-xl' : 'h-9 rounded-md'} flex items-center justify-center border transition-colors ${isActive ? 'bg-primary text-primary-foreground border-primary shadow-sm' : 'bg-background text-muted-foreground border-border hover:bg-muted'}`}
            data-testid={tab.testId}
          >
            <Icon size={dense ? 15 : 16} />
          </button>
        );
      })}
    </div>
  );
}

function AdvancedOverlayPanel({ title, onClose, children, testIdPrefix = 'advanced-controls-side-panel' }) {
  return (
    <div
      className="fixed left-[408px] top-24 z-50 w-[340px] rounded-xl border bg-background shadow-xl"
      data-testid={testIdPrefix}
    >
      <div className="flex items-center justify-between border-b px-4 py-3">
        <p className="text-sm font-semibold" data-testid={`${testIdPrefix}-title`}>{title}</p>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onClose}
          data-testid={`${testIdPrefix}-close-button`}
        >
          <X size={14} />
        </Button>
      </div>
      <div className="max-h-[70vh] overflow-y-auto p-4" data-testid={`${testIdPrefix}-content`}>
        {children}
      </div>
    </div>
  );
}

const normalizeTrouserLengthMode = (value = 'full') => {
  const normalized = String(value || 'full').toLowerCase();
  return normalized === 'short' || normalized === 'shorts' ? 'short' : 'full';
};

const DENSE_FIELD_STACK_CLASS = 'space-y-1';
const DENSE_FIELD_ROW_CLASS = 'flex items-center justify-between gap-2';
const DENSE_LABEL_CLASS = 'text-[9px] font-medium uppercase tracking-[0.16em] text-slate-500';
const DENSE_DRAWN_LABEL_CLASS = 'text-[10px] font-medium uppercase tracking-[0.14em] text-slate-500';
const DENSE_INPUT_CLASS = 'h-7 rounded-md border-slate-200 bg-white px-2 text-[12px] leading-none shadow-sm md:text-[12px]';
const DENSE_SELECT_TRIGGER_CLASS = 'h-7 rounded-md border-slate-200 bg-white text-[11px] shadow-sm';
const DENSE_DRAWN_PANEL_CLASS = 'rounded-xl border border-slate-200 bg-white p-2 shadow-sm';
const DENSE_DRAWN_INPUT_CLASS = 'h-7 rounded-md border-slate-200 bg-white px-2 text-[12px] leading-none shadow-sm md:text-[12px]';
const DENSE_DRAWN_STEPPER_CLASS = 'h-[13px] w-4 min-w-0 rounded-[3px] border-slate-200 bg-white p-0 text-slate-600 hover:bg-slate-50 hover:text-sky-700';
const DENSE_SUPPORT_TEXT_CLASS = 'text-[10px] leading-4 text-slate-500';

export default function LeftPanel({ 
  measurements, 
  referenceMeasurements = {},
  onMeasurementChange,
  onAdjustDrawnMeasurement,
  onCommitDrawnMeasurement,
  onResetCurveHandles,
  onRecomputeJacketCollar,
  onRecomputeShirtCollarBand,
  onAutoShirtSideSeam,
  onAutoShirtBackYokeDartCurve,
  onAutoShirtYokePanelDartCurve,
  onAutoShirtArmholeCurves,
  onAutoShirtSleeveCurvesFpBp,
  modifiedParams = [],
  canEdit = true,
  garmentType = 'trouser',
  garmentStatus = 'ready',
  patternData = null,
  geometryEditState = 'parametric',
  selectedFreePoints = [],
}) {
  const [activeAdvancedControl, setActiveAdvancedControl] = useState(null);
  const [activeShirtPanel, setActiveShirtPanel] = useState('shirt-body');
  const [activeJacketPanel, setActiveJacketPanel] = useState('body');
  const [activeJacketAdvancedControl, setActiveJacketAdvancedControl] = useState(null);
  const [activeTrouserPanel, setActiveTrouserPanel] = useState('base');
  const [activeTrouserAdvancedControl, setActiveTrouserAdvancedControl] = useState(null);
  const [initializedOverrideKeys, setInitializedOverrideKeys] = useState({});
  const [bufferedInputValues, setBufferedInputValues] = useState({});
  const [activeBufferedInputs, setActiveBufferedInputs] = useState({});
  const bufferedCommitTimersRef = useRef({});
  const isModified = (key) => modifiedParams.includes(key);
  const prettyGarmentType = garmentType.charAt(0).toUpperCase() + garmentType.slice(1);
  const formatMeasurement = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '0.000';
    return numeric.toFixed(3);
  };
  const formatDrawnInputValue = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '';
    return Number(numeric.toFixed(2)).toString();
  };
  const formatDeviation = (requestedValue, drawnValue) => {
    const requestedNumeric = Number(requestedValue);
    const drawnNumeric = Number(drawnValue);
    if (!Number.isFinite(requestedNumeric) || !Number.isFinite(drawnNumeric)) return null;
    const delta = drawnNumeric - requestedNumeric;
    const sign = delta > 0 ? '+' : '';
    return `${sign}${delta.toFixed(3)}`;
  };
  const isFreeEditedDraft = geometryEditState === 'free-edited';
  const referenceValueMap = referenceMeasurements || {};
  const selectedFreePointsCount = Array.isArray(selectedFreePoints) ? selectedFreePoints.length : 0;

  const renderGeometryEditingPanel = (testIdPrefix = `${garmentType}-geometry-edit-state-panel`, options = {}) => options.cadDense ? (
    <div className="h-8 rounded-md border border-slate-200 bg-white px-2 shadow-sm flex items-center gap-1.5" data-testid={testIdPrefix}>
      <Label className="min-w-0 flex-1 truncate text-[9px] font-semibold uppercase text-slate-600" data-testid={`${testIdPrefix}-title`}>
        Geometry Editing
      </Label>
      <span className="text-[9px] font-semibold uppercase text-sky-700" data-testid={`${testIdPrefix}-enabled-chip`}>
        On
      </span>
      <span
        className={`whitespace-nowrap rounded-[3px] border px-1.5 py-0.5 text-[9px] font-semibold uppercase ${isFreeEditedDraft ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-emerald-300 bg-emerald-50 text-emerald-700'}`}
        data-testid={`${testIdPrefix}-mode-chip`}
      >
        {isFreeEditedDraft ? 'Free-Edited Draft' : 'Parametric Draft'}
      </span>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-sm text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
              aria-label="Geometry editing information"
              data-testid={`${testIdPrefix}-info-button`}
            >
              <Info size={12} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" align="start" className="max-w-[280px] space-y-1.5 bg-slate-900 px-3 py-2 text-[11px] leading-4 text-white" data-testid={`${testIdPrefix}-info-tooltip`}>
            <p data-testid={`${testIdPrefix}-helper-text`}>
              {isFreeEditedDraft
                ? 'Inputs stay live. Parametric changes redraw the draft, while saved geometry edits keep reapplying as local overrides.'
                : 'Inputs are driving the draft directly. Once you edit geometry on canvas, those edits persist as local overrides on top of the parametric draft.'}
            </p>
            {(options.showFreePointHelp || options.showJacketPointHelp) && (
              <p data-testid={`${testIdPrefix}-${options.freePointGarmentLabel || 'jacket'}-point-help-text`}>
                Click a {options.freePointGarmentLabel || 'jacket'} point or lasso-select multiple points, then use the canvas nudge pad.
              </p>
            )}
            {options.selectedPointCount > 0 && (
              <p className="font-semibold text-sky-200" data-testid={`${testIdPrefix}-selected-point-count-text`}>
                Selected points: {options.selectedPointCount}
              </p>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  ) : (
    <div className={`${options.compact ? 'rounded-xl border-slate-200 bg-white/90 p-2.5 space-y-2 shadow-sm' : 'rounded-md bg-muted/20 p-3 space-y-3'} border`} data-testid={testIdPrefix}>
      <div className="flex items-center justify-between gap-3">
        <Label className={`${options.compact ? 'text-[10px] uppercase tracking-[0.18em] text-slate-500' : 'text-xs text-muted-foreground'}`}>Geometry Editing</Label>
        <div className="flex items-center gap-2">
          <span className={`${options.compact ? 'text-[10px] px-2 py-0.5' : 'text-[11px] px-2 py-1'} font-semibold rounded-full border bg-sky-50 text-sky-700 border-sky-300`} data-testid={`${testIdPrefix}-enabled-chip`}>
            ON
          </span>
          <span
            className={`${options.compact ? 'text-[10px] px-2 py-0.5' : 'text-[11px] px-2 py-1'} font-semibold rounded-full border ${isFreeEditedDraft ? 'bg-amber-50 text-amber-700 border-amber-300' : 'bg-emerald-50 text-emerald-700 border-emerald-300'}`}
            data-testid={`${testIdPrefix}-mode-chip`}
          >
            {isFreeEditedDraft ? 'FREE-EDITED DRAFT' : 'PARAMETRIC DRAFT'}
          </span>
        </div>
      </div>
      <p className={`${options.compact ? 'text-[10px] leading-4 text-slate-500' : 'text-[11px] leading-relaxed text-muted-foreground'}`} data-testid={`${testIdPrefix}-helper-text`}>
        {isFreeEditedDraft
          ? 'Inputs stay live. Parametric changes redraw the draft, while saved geometry edits keep reapplying as local overrides.'
          : 'Inputs are driving the draft directly. Once you edit geometry on canvas, those edits persist as local overrides on top of the parametric draft.'}
      </p>
      {(options.showFreePointHelp || options.showJacketPointHelp) && (
        <p className={`${options.compact ? 'text-[10px] leading-4 text-slate-500' : 'text-[11px] leading-relaxed text-muted-foreground'}`} data-testid={`${testIdPrefix}-${options.freePointGarmentLabel || 'jacket'}-point-help-text`}>
          {options.freePointGarmentLabel || 'Jacket'} point nudging stays available directly on canvas. Click a point or lasso-select multiple points, then use the nudge pad.
        </p>
      )}
      {options.selectedPointCount > 0 && (
        <p className={`${options.compact ? 'text-[10px]' : 'text-[11px]'} font-medium text-sky-700`} data-testid={`${testIdPrefix}-selected-point-count-text`}>
          Selected points: {options.selectedPointCount}
        </p>
      )}
    </div>
  );

  useEffect(() => {
    if (garmentType !== 'shirt') {
      setActiveAdvancedControl(null);
      setActiveShirtPanel('shirt-body');
    } else if (
      String(patternData?.geometryVersion || '').toLowerCase() !== 'shirt_geom_v3'
      && activeShirtPanel === 'shirt-collar'
    ) {
      setActiveShirtPanel('shirt-body');
    }

    if (garmentType !== 'jacket') {
      setActiveJacketPanel('body');
      setActiveJacketAdvancedControl(null);
    } else if (!['body', 'sleeve', 'panel', 'pocket', 'button', 'lapel', 'formula'].includes(activeJacketPanel)) {
      setActiveJacketPanel('body');
    }

    if (garmentType !== 'trouser') {
      setActiveTrouserPanel('base');
      setActiveTrouserAdvancedControl(null);
    } else if (!['base', 'front', 'back', 'geometry'].includes(activeTrouserPanel)) {
      setActiveTrouserPanel('base');
    }
  }, [garmentType, activeTrouserPanel, activeJacketPanel, activeShirtPanel, patternData?.geometryVersion]);

  useEffect(() => () => {
    Object.values(bufferedCommitTimersRef.current || {}).forEach((timerId) => {
      window.clearTimeout(timerId);
    });
    bufferedCommitTimersRef.current = {};
  }, []);

  const clearBufferedCommitTimer = (fieldKey) => {
    const timerId = bufferedCommitTimersRef.current?.[fieldKey];
    if (timerId) {
      window.clearTimeout(timerId);
      delete bufferedCommitTimersRef.current[fieldKey];
    }
  };

  const beginBufferedInput = (fieldKey, externalValue) => {
    setActiveBufferedInputs((prev) => ({
      ...prev,
      [fieldKey]: true,
    }));
    setBufferedInputValues((prev) => ({
      ...prev,
      [fieldKey]: externalValue === undefined || externalValue === null ? '' : String(externalValue),
    }));
  };

  const clearBufferedInput = (fieldKey) => {
    clearBufferedCommitTimer(fieldKey);
    setActiveBufferedInputs((prev) => {
      if (!prev[fieldKey]) return prev;
      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
    setBufferedInputValues((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, fieldKey)) return prev;
      const next = { ...prev };
      delete next[fieldKey];
      return next;
    });
  };

  const getBufferedInputValue = (fieldKey, externalValue) => {
    if (activeBufferedInputs[fieldKey]) {
      return bufferedInputValues[fieldKey] ?? '';
    }
    return externalValue ?? '';
  };

  const commitMeasurementValue = (fieldKey, rawValue, options = {}) => {
    if (options.readOnly || !canEdit) return;

    const trimmedValue = String(rawValue ?? '').trim();
    if (trimmedValue === '') return;

    const nextValue = typeof options.transformValue === 'function'
      ? options.transformValue(trimmedValue)
      : trimmedValue;

    if (typeof options.onCommit === 'function') {
      options.onCommit(nextValue);
      return;
    }

    onMeasurementChange(options.commitKey || fieldKey, nextValue);
  };

  const scheduleBufferedCommit = (fieldKey, rawValue, options = {}) => {
    clearBufferedCommitTimer(fieldKey);

    if (options.readOnly || !canEdit) return;

    bufferedCommitTimersRef.current[fieldKey] = window.setTimeout(() => {
      delete bufferedCommitTimersRef.current[fieldKey];
      commitMeasurementValue(fieldKey, rawValue, options);
    }, 500);
  };

  const commitBufferedInput = (fieldKey, externalValue, options = {}) => {
    if (options.readOnly || !canEdit) {
      clearBufferedInput(fieldKey);
      return;
    }

    const rawValue = activeBufferedInputs[fieldKey]
      ? bufferedInputValues[fieldKey]
      : (externalValue === undefined || externalValue === null ? '' : String(externalValue));

    clearBufferedInput(fieldKey);
    commitMeasurementValue(fieldKey, rawValue, options);
  };

  const getBufferedInputHandlers = (fieldKey, externalValue, options = {}) => ({
    value: getBufferedInputValue(fieldKey, externalValue),
    onFocus: () => {
      beginBufferedInput(fieldKey, externalValue);
      if (typeof options.onFocus === 'function') {
        options.onFocus();
      }
    },
    onChange: (event) => {
      if (options.readOnly) return;
      const nextValue = event?.target?.value ?? '';
      setActiveBufferedInputs((prev) => ({
        ...prev,
        [fieldKey]: true,
      }));
      setBufferedInputValues((prev) => ({
        ...prev,
        [fieldKey]: nextValue,
      }));
      if (!options.commitOnBlurOnly) {
        scheduleBufferedCommit(fieldKey, nextValue, options);
      }
    },
    onBlur: () => commitBufferedInput(fieldKey, externalValue, options),
    onKeyDown: (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.currentTarget.blur();
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        clearBufferedInput(fieldKey);
        event.currentTarget.blur();
      }
    },
  });

  const renderDrawnAdjustableControl = ({
    key,
    label,
    drawnValue,
    seedValue,
    step = 0.125,
    helperText = '',
    compact = false,
    cadDense = false,
    hideSeed = false,
    advancedKey = null,
    onOpenAdvanced = null,
  }) => {
    const numericDrawn = Number(drawnValue);
    if (!Number.isFinite(numericDrawn)) return null;

    const numericSeed = Number(seedValue);
    const deviation = formatDeviation(seedValue, numericDrawn);
    const safeSeedText = Number.isFinite(numericSeed) ? `${numericSeed.toFixed(3)}"` : '—';

    return (
      <div
        key={`${garmentType}-${key}-drawn-control`}
        className={cadDense ? DENSE_FIELD_STACK_CLASS : `${compact ? 'space-y-1.5' : 'space-y-2'}`}
        data-testid={`${garmentType}-${key}-drawn-control-row`}
      >
        <div className={`min-w-0 ${cadDense ? '' : 'space-y-0.5'}`}>
          <div className={cadDense ? '' : DENSE_FIELD_ROW_CLASS}>
            <p className={`${cadDense ? `${DENSE_DRAWN_LABEL_CLASS} truncate leading-[11px]` : compact ? 'text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500' : 'text-[11px] tracking-wide text-muted-foreground'} ${cadDense ? '' : 'uppercase'}`} data-testid={`${garmentType}-${key}-drawn-control-label`}>
              {label}
            </p>
          </div>
          <div className={`flex items-center ${cadDense ? 'gap-1' : 'gap-2.5'}`}>
            <Input
              type="number"
              step={step}
              inputMode="decimal"
              {...getBufferedInputHandlers(`${garmentType}-${key}-drawn`, formatDrawnInputValue(numericDrawn), {
                commitOnBlurOnly: true,
                onCommit: (nextValue) => onCommitDrawnMeasurement?.(key, nextValue),
                onFocus: () => {
                  if (advancedKey && typeof onOpenAdvanced === 'function') {
                    onOpenAdvanced(advancedKey);
                  }
                },
              })}
              disabled={!canEdit || !onCommitDrawnMeasurement}
              data-testid={`${garmentType}-${key}-drawn-control-input`}
              className={`${cadDense ? `${DENSE_DRAWN_INPUT_CLASS} min-w-0 flex-1 text-[12px]` : compact ? 'h-8 flex-1 rounded-lg px-2 text-[13px] leading-none md:text-[13px] md:leading-none' : 'h-9 w-[124px] text-sm'} border-slate-200 bg-white text-right font-sans [font-variant-numeric:normal] text-slate-900`}
            />
            <div className={`flex flex-col ${cadDense ? 'gap-px' : 'gap-1'}`}>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={cadDense ? DENSE_DRAWN_STEPPER_CLASS : compact ? 'h-7 w-7 rounded-lg' : 'h-8 w-8 rounded-full'}
                onClick={() => onAdjustDrawnMeasurement?.(key, step)}
                disabled={!canEdit || !onAdjustDrawnMeasurement}
                data-testid={`${garmentType}-${key}-drawn-control-increase-button`}
              >
                <ChevronUp size={cadDense ? 9 : compact ? 13 : 14} />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={cadDense ? DENSE_DRAWN_STEPPER_CLASS : compact ? 'h-7 w-7 rounded-lg' : 'h-8 w-8 rounded-full'}
                onClick={() => onAdjustDrawnMeasurement?.(key, -step)}
                disabled={!canEdit || !onAdjustDrawnMeasurement}
                data-testid={`${garmentType}-${key}-drawn-control-decrease-button`}
              >
                <ChevronDown size={cadDense ? 9 : compact ? 13 : 14} />
              </Button>
            </div>
          </div>
          {!cadDense && !hideSeed && (
            <p className="text-[11px] text-muted-foreground" data-testid={`${garmentType}-${key}-drawn-control-seed-text`}>
              Seed: {safeSeedText}{deviation ? ` • Δ ${deviation}"` : ''}
            </p>
          )}
          {!cadDense && helperText ? (
            <p className={`${compact ? 'text-[10px] leading-4 text-slate-500' : 'text-[11px] text-muted-foreground'}`} data-testid={`${garmentType}-${key}-drawn-control-helper-text`}>
              {helperText}
            </p>
          ) : null}
        </div>
      </div>
    );
  };

  const renderDrawnMeasurementPanel = (rows = [], testIdPrefix = `${garmentType}-drawn-measurements-panel`, options = {}) => {
    const visibleRows = rows.filter(Boolean);
    if (!visibleRows.length) return null;

    return (
      <div className={`${options.cadDense ? `${DENSE_DRAWN_PANEL_CLASS} space-y-2` : options.compact ? 'rounded-2xl border border-slate-200 bg-white p-2.5 space-y-2.5 shadow-sm' : 'rounded-2xl border border-slate-200 bg-white p-3 space-y-3 shadow-sm'}`} data-testid={testIdPrefix}>
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <p className={`${options.cadDense ? 'text-[10px] tracking-[0.16em]' : options.compact ? 'text-[12px] tracking-[0.14em]' : 'text-sm'} font-semibold uppercase text-slate-700`} data-testid={`${testIdPrefix}-title`}>
              Drawn Measurements
            </p>
            <span className={`${options.cadDense ? 'rounded-[3px] px-1.5 py-px text-[8px]' : `${options.compact ? 'px-2 py-0.5' : 'px-2 py-1'} rounded-full text-[10px] tracking-wide`} border border-slate-200 bg-slate-50 font-semibold uppercase text-emerald-700`} data-testid={`${testIdPrefix}-primary-chip`}>
              Primary
            </span>
          </div>
          {!options.hideHelperText && (
            <p className={`${options.compact ? DENSE_SUPPORT_TEXT_CLASS : 'text-[11px] leading-relaxed text-slate-500'}`} data-testid={`${testIdPrefix}-helper-text`}>
              {options.helperText || 'These controls follow the draft on canvas. Type a value and leave the field to apply it, or use the arrows for 1/8" nudges.'}
            </p>
          )}
        </div>
        <div className={options.cadDense ? 'grid grid-cols-2 gap-x-2 gap-y-2' : options.compact ? 'grid grid-cols-2 gap-2' : 'space-y-2'}>
          {visibleRows.map((row) => renderDrawnAdjustableControl({
            ...row,
            compact: options.compact,
            cadDense: options.cadDense,
            hideSeed: options.hideSeed,
          }))}
        </div>
      </div>
    );
  };

  const renderInput = (id, label, value, options = {}) => (
    <div key={id} className={DENSE_FIELD_STACK_CLASS}>
      <Label className={DENSE_LABEL_CLASS}>{label}</Label>
      <Input
        type="number"
        step="0.125"
        {...getBufferedInputHandlers(id, value, {
          commitOnBlurOnly: options.commitOnBlurOnly,
        })}
        disabled={!canEdit}
        data-testid={`${id}-input`}
        className={`${DENSE_INPUT_CLASS} ${isModified(id) ? 'border-orange-400 bg-orange-50/30' : ''} ${!canEdit ? 'bg-muted cursor-not-allowed' : ''}`}
      />
    </div>
  );

  const renderTrouserInput = (id, label, value, options = {}) => (
    <div key={id} className={DENSE_FIELD_STACK_CLASS}>
      <div className={DENSE_FIELD_ROW_CLASS}>
        <Label className={DENSE_LABEL_CLASS}>{label}</Label>
        {options.advancedKey && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-4 w-4 p-0 text-slate-500"
            onClick={() => setActiveTrouserAdvancedControl(options.advancedKey)}
            disabled={!canEdit}
            data-testid={`trouser-${id}-advanced-trigger-button`}
          >
            <SlidersHorizontal size={10} />
          </Button>
        )}
      </div>
      <Input
        type="number"
        step="0.125"
        {...getBufferedInputHandlers(id, value, {
          commitOnBlurOnly: true,
          readOnly: options.readOnly,
          transformValue: options.transformValue,
          onFocus: () => {
            if (options.advancedKey) setActiveTrouserAdvancedControl(options.advancedKey);
          },
        })}
        disabled={!canEdit || options.readOnly}
        data-testid={`trouser-${id}-input`}
        className={`${DENSE_INPUT_CLASS} ${isModified(id) ? 'border-orange-400 bg-orange-50/30' : ''} ${(!canEdit || options.readOnly) ? 'bg-muted cursor-not-allowed' : ''}`}
      />
      {options.drawnText && (
        <p className="text-[10px] text-emerald-600 font-medium" data-testid={`trouser-${id}-drawn-value-text`}>
          {options.drawnText}
        </p>
      )}
    </div>
  );

  const trouserPanelTabs = [
    { key: 'base', label: 'Base Measurements', icon: Ruler, testId: 'trouser-panel-tab-base' },
    { key: 'front', label: 'Front Pattern', icon: RulerDimensionLine, testId: 'trouser-panel-tab-front' },
    { key: 'back', label: 'Back Pattern', icon: Layers, testId: 'trouser-panel-tab-back' },
    { key: 'geometry', label: 'Geometry Notes', icon: SigmaSquare, testId: 'trouser-panel-tab-geometry' },
  ];

  if (garmentType === 'shirt') {
    const matchingSummary = patternData?.matchingSummary || {};
    const isYokeEnabled = measurements?.yokeEnabled !== false && measurements?.yokeEnabled !== 0 && measurements?.yokeEnabled !== '0';
    const isYokeDartActive = isYokeEnabled && Number(measurements?.yokeDartWidth || 0) > 0;
    const isFishDartEnabled = measurements?.fishDartEnabled !== false && measurements?.fishDartEnabled !== 0 && measurements?.fishDartEnabled !== '0';
    const backStyle = String(measurements?.backStyle || 'none');
    const isShortSleeve = measurements?.sleeveStyle === 'short';

    const geometryVersionNormalized = String(patternData?.geometryVersion || '').toLowerCase();
    const isShirtGeometryV3 = geometryVersionNormalized === 'shirt_geom_v3' || geometryVersionNormalized === 'shirt_geom_v4';
    const isLadiesShirt = String(measurements?.shirtBodyStyle || 'gents').toLowerCase() === 'ladies';
    const shirtPanelTabs = [
      { key: 'shirt-body', label: 'Body Inputs', icon: Shirt, testId: 'shirt-panel-tab-body' },
      { key: 'shirt-dart', label: 'Back Fish Dart', icon: Fish, testId: 'shirt-panel-tab-dart' },
      ...(isLadiesShirt ? [
        { key: 'shirt-bust-dart', label: 'Bust Dart', icon: ScissorsLineDashed, testId: 'shirt-panel-tab-bust-dart' },
        { key: 'shirt-front-fish-dart', label: 'Front Fish Dart', icon: Fish, testId: 'shirt-panel-tab-front-fish-dart' },
      ] : []),
      { key: 'shirt-back-style', label: 'Back Style Pleat', icon: FoldVertical, testId: 'shirt-panel-tab-back-style' },
      { key: 'shirt-yoke', label: 'Yoke Inputs', icon: ScissorsLineDashed, testId: 'shirt-panel-tab-yoke' },
      { key: 'shirt-sleeve', label: 'Sleeve Inputs', icon: RulerDimensionLine, testId: 'shirt-panel-tab-sleeve' },
      ...(isShirtGeometryV3 ? [{ key: 'shirt-collar', label: 'Collar', icon: PanelTop, testId: 'shirt-panel-tab-collar' }] : []),
      { key: 'shirt-buttons', label: 'Button + Placket', icon: CircleDot, testId: 'shirt-panel-tab-buttons' },
      { key: 'shirt-formula', label: 'Point Map + Correspondence', icon: SigmaSquare, testId: 'shirt-panel-tab-formula' },
    ];

    const resolveDrawnValueText = (id) => {
      const plainMap = {
        neck: matchingSummary.totalNeckFull,
        collarBandNeckLength: matchingSummary.collarBandNeckLength,
        chest: matchingSummary.totalChestFull,
        waist: matchingSummary.totalWaistFull,
        belly: matchingSummary.totalBellyFull,
        seat: matchingSummary.totalSeatFull,
        shoulder: matchingSummary.shoulderHorizontalFull,
        acrossBack: matchingSummary.acrossBackFull,
        acrossFront: matchingSummary.acrossFrontFull,
        shirtLengthBack: matchingSummary.shirtLengthBackDrawn,
        shirtLengthFront: matchingSummary.shirtLengthFrontDrawn,
        sleeveDistToElbow: matchingSummary.sleeveDistToElbow,
      };

      if (id === 'armholeScyeDepth') {
        if (!matchingSummary.scyeDepthAverage) return null;
        const deviation = formatDeviation(measurements?.armholeScyeDepth, matchingSummary.scyeDepthAverage);
        return `Drawn avg: ${matchingSummary.scyeDepthAverage}" (F: ${matchingSummary.scyeDepthFront}", B: ${matchingSummary.scyeDepthBack}")${deviation ? ` | Δ ${deviation}"` : ''}`;
      }

      const value = plainMap[id];
      if (value === undefined || value === null) return null;
      const deviation = formatDeviation(measurements?.[id], value);
      return `Drawn full: ${value}"${deviation ? ` | Δ ${deviation}"` : ''}`;
    };

    const shirtDrawnRows = [
      { key: 'neck', label: 'Neck/Collar', drawnValue: matchingSummary.totalNeckFull, seedValue: referenceValueMap.neck, advancedKey: 'neck', onOpenAdvanced: setActiveAdvancedControl },
      {
        key: 'chest',
        label: 'Chest',
        drawnValue: matchingSummary.totalChestFull,
        seedValue: referenceValueMap.chest,
        advancedKey: 'chest',
        onOpenAdvanced: setActiveAdvancedControl,
      },
      ...(isLadiesShirt ? [{
        key: 'bust',
        label: 'Bust (full)',
        drawnValue: matchingSummary.targetBustFull ?? measurements?.bust,
        seedValue: measurements?.bust,
        helperText: matchingSummary?.bustHalfDrawn != null
          ? `Half at dart: ${Number(matchingSummary.bustHalfDrawn).toFixed(3)}" (target ${Number((measurements?.bust || 0) / 2).toFixed(3)}")`
          : '',
      }] : []),
      { key: 'waist', label: 'Waist', drawnValue: matchingSummary.totalWaistFull, seedValue: referenceValueMap.waist, advancedKey: 'waist', onOpenAdvanced: setActiveAdvancedControl },
      { key: 'belly', label: 'Belly', drawnValue: matchingSummary.totalBellyFull, seedValue: referenceValueMap.belly, advancedKey: 'belly', onOpenAdvanced: setActiveAdvancedControl },
      { key: 'seat', label: 'Seat/Hem', drawnValue: matchingSummary.totalSeatFull, seedValue: referenceValueMap.seat, advancedKey: 'seat', onOpenAdvanced: setActiveAdvancedControl },
      { key: 'shoulder', label: 'Shoulder', drawnValue: matchingSummary.shoulderHorizontalFull, seedValue: referenceValueMap.shoulder, advancedKey: 'shoulder', onOpenAdvanced: setActiveAdvancedControl },
      { key: 'acrossBack', label: 'X-Back', drawnValue: matchingSummary.acrossBackFull, seedValue: referenceValueMap.acrossBack, advancedKey: 'acrossBack', onOpenAdvanced: setActiveAdvancedControl },
      { key: 'acrossFront', label: 'X-Front', drawnValue: matchingSummary.acrossFrontFull, seedValue: referenceValueMap.acrossFront, advancedKey: 'acrossFront', onOpenAdvanced: setActiveAdvancedControl },
      {
        key: 'armholeScyeDepth',
        label: 'Armhole Scye Depth',
        drawnValue: matchingSummary.scyeDepthAverage,
        seedValue: referenceValueMap.armholeScyeDepth,
        advancedKey: 'armhole',
        onOpenAdvanced: setActiveAdvancedControl,
        helperText: matchingSummary.scyeDepthFront !== undefined && matchingSummary.scyeDepthBack !== undefined
          ? `Front ${Number(matchingSummary.scyeDepthFront || 0).toFixed(3)}" • Back ${Number(matchingSummary.scyeDepthBack || 0).toFixed(3)}"`
          : '',
      },
      { key: 'shirtLengthBack', label: 'Shirt Length Back', drawnValue: matchingSummary.shirtLengthBackDrawn, seedValue: referenceValueMap.shirtLengthBack, advancedKey: 'shirtLength', onOpenAdvanced: setActiveAdvancedControl },
      { key: 'shirtLengthFront', label: 'Shirt Length Front', drawnValue: matchingSummary.shirtLengthFrontDrawn, seedValue: referenceValueMap.shirtLengthFront, advancedKey: 'shirtLength', onOpenAdvanced: setActiveAdvancedControl },
      {
        key: isShortSleeve ? 'shortSleeveLength' : 'sleeveLength',
        label: 'Sleeve Length',
        drawnValue: matchingSummary.sleeveDisplayedLength,
        seedValue: referenceValueMap[isShortSleeve ? 'shortSleeveLength' : 'sleeveLength'],
        advancedKey: measurements.sleeveStyle !== 'short' ? 'sleeve' : null,
        onOpenAdvanced: measurements.sleeveStyle !== 'short' ? setActiveAdvancedControl : null,
      },
      { key: 'bicepWidth', label: 'Bicep', drawnValue: matchingSummary.sleeveBicepUnwrappedDrawn, seedValue: referenceValueMap.bicepWidth },
      ...(!isShortSleeve ? [{
        key: 'elbowWidth',
        label: 'Elbow',
        drawnValue: matchingSummary.sleeveElbowUnwrappedDrawn,
        seedValue: referenceValueMap.elbowWidth,
        advancedKey: 'elbow',
        onOpenAdvanced: setActiveAdvancedControl,
      }] : []),
      {
        key: isShortSleeve ? 'shortSleeveHem' : 'maxCuffWidth',
        label: isShortSleeve ? 'Sleeve Hem' : 'Wrist/Cuff',
        drawnValue: matchingSummary.sleeveHemUnwrappedDrawn,
        seedValue: referenceValueMap[isShortSleeve ? 'shortSleeveHem' : 'maxCuffWidth'],
      },
    ];

    const clampShirtFrontSplit = (frontValue, totalHalf, min = 0.75) => {
      const targetHalf = Math.max(min * 2, Number(totalHalf || 0));
      const candidate = Number(frontValue || targetHalf / 2);
      return Math.min(targetHalf - min, Math.max(min, candidate));
    };

    const waistHalf = Number(measurements?.waist || 0) / 2;
    const bellyHalf = Number(measurements?.belly || 0) / 2;
    const chestHalf = Number(measurements?.chest || 0) / 2;
    const seatHalf = Number(measurements?.seat || 0) / 2;

    const frontChestResolved = clampShirtFrontSplit(
      Number.isFinite(Number(measurements?.frontChestSplit)) ? Number(measurements.frontChestSplit) : Number(matchingSummary.frontChestHalf || chestHalf / 2),
      chestHalf,
    );
    const backChestResolved = chestHalf - frontChestResolved;

    const frontWaistResolvedShirt = clampShirtFrontSplit(
      Number.isFinite(Number(measurements?.frontWaistSplit))
        ? Number(measurements.frontWaistSplit)
        : Number(matchingSummary.frontWaistHalfNet ?? matchingSummary.frontWaistHalfRaw ?? waistHalf / 2),
      waistHalf,
    );
    const backWaistResolvedShirt = Math.max(0.75, waistHalf - frontWaistResolvedShirt);

    const frontBellyResolvedShirt = clampShirtFrontSplit(
      Number.isFinite(Number(measurements?.frontBellySplit))
        ? Number(measurements.frontBellySplit)
        : Number(matchingSummary.frontBellyHalfNet ?? matchingSummary.frontBellyHalfRaw ?? bellyHalf / 2),
      bellyHalf,
    );
    const backBellyResolvedShirt = Math.max(0.75, bellyHalf - frontBellyResolvedShirt);

    const frontSeatResolvedShirt = clampShirtFrontSplit(
      Number.isFinite(Number(measurements?.frontSeatSplit)) ? Number(measurements.frontSeatSplit) : Number(matchingSummary.frontSeatHalf || seatHalf / 2),
      seatHalf,
    );
    const backSeatResolvedShirt = seatHalf - frontSeatResolvedShirt;

    const renderSideSeamSegmentsInfo = () => (
      <div className="space-y-1 text-[11px] text-muted-foreground" data-testid="shirt-side-seam-segment-lengths-small-text">
        <p>Side seam segments (F / B / Δ):</p>
        <p>
          C→W: {Number(matchingSummary.frontSideChestToWaist || 0).toFixed(3)}" /
          {' '}{Number(matchingSummary.backSideChestToWaist || 0).toFixed(3)}" /
          {' '}{Math.abs(Number(matchingSummary.sideChestToWaistDelta || 0)).toFixed(3)}"
        </p>
        <p>
          W→B: {Number(matchingSummary.frontSideWaistToBelly || 0).toFixed(3)}" /
          {' '}{Number(matchingSummary.backSideWaistToBelly || 0).toFixed(3)}" /
          {' '}{Math.abs(Number(matchingSummary.sideWaistToBellyDelta || 0)).toFixed(3)}"
        </p>
        <p>
          B→H: {Number(matchingSummary.frontSideBellyToSeat || 0).toFixed(3)}" /
          {' '}{Number(matchingSummary.backSideBellyToSeat || 0).toFixed(3)}" /
          {' '}{Math.abs(Number(matchingSummary.sideBellyToSeatDelta || 0)).toFixed(3)}"
        </p>
      </div>
    );

    const renderShirtInput = (id, label, value, options = {}) => (
      <div key={id} className={DENSE_FIELD_STACK_CLASS}>
        <div className={DENSE_FIELD_ROW_CLASS}>
          <Label className={DENSE_LABEL_CLASS}>{label}</Label>
          {options.advancedKey && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-4 w-4 p-0 text-slate-500"
              onClick={() => setActiveAdvancedControl(options.advancedKey)}
              disabled={!canEdit}
              data-testid={`shirt-${id}-advanced-trigger-button`}
            >
              <SlidersHorizontal size={10} />
            </Button>
          )}
        </div>
        <Input
          type="number"
          step={options.step || '0.125'}
          {...getBufferedInputHandlers(id, value, {
            readOnly: options.readOnly,
            onFocus: () => {
              if (options.advancedKey) setActiveAdvancedControl(options.advancedKey);
            },
          })}
          disabled={!canEdit || options.readOnly}
          data-testid={`shirt-${id}-input`}
          className={`${DENSE_INPUT_CLASS} ${isModified(id) ? 'border-orange-400 bg-orange-50/30' : ''} ${(!canEdit || options.readOnly) ? 'bg-muted cursor-not-allowed' : ''}`}
        />
        {resolveDrawnValueText(id) && (
          <p className="text-[10px] text-emerald-600 font-medium" data-testid={`shirt-${id}-drawn-value-text`}>
            {resolveDrawnValueText(id)}
          </p>
        )}
      </div>
    );

    const renderOverrideControl = ({
      section,
      label,
      enabledKey,
      valueKey,
      autoValue,
      drawnValue,
    }) => {
      const enabled = Boolean(measurements?.[enabledKey]);
      const hasManualValue = measurements?.[valueKey] !== null && measurements?.[valueKey] !== undefined && measurements?.[valueKey] !== '';
      const currentValue = hasManualValue ? measurements?.[valueKey] : '';

      const handleOverrideToggle = (checked) => {
        if (!checked) {
          onMeasurementChange(enabledKey, false);
          return;
        }

        const drawnNumeric = Number(drawnValue);
        const autoNumeric = Number(autoValue);
        const seedValue = Number.isFinite(drawnNumeric)
          ? drawnNumeric
          : (Number.isFinite(autoNumeric) ? autoNumeric : 0);

        if (!initializedOverrideKeys[valueKey]) {
          onMeasurementChange(valueKey, Number(seedValue.toFixed(3)));
          setInitializedOverrideKeys((prev) => ({ ...prev, [valueKey]: true }));
        }

        onMeasurementChange(enabledKey, true);
      };

      return (
        <div className="rounded-md border p-3 space-y-2" data-testid={`shirt-${section}-${valueKey}-override-row`}>
          <div className="flex items-center justify-between gap-3">
            <Label className="text-xs text-muted-foreground">{label}</Label>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground">Override</span>
              <Switch
                checked={enabled}
                onCheckedChange={handleOverrideToggle}
                disabled={!canEdit}
                data-testid={`shirt-${section}-${valueKey}-override-switch`}
              />
            </div>
          </div>

          <p className="text-[11px] text-muted-foreground" data-testid={`shirt-${section}-${valueKey}-auto-text`}>
            Auto: {Number(autoValue ?? 0).toFixed(3)}"
          </p>

          {enabled && (
            <Input
              type="number"
              step="0.125"
              {...getBufferedInputHandlers(valueKey, currentValue)}
              disabled={!canEdit}
              data-testid={`shirt-${section}-${valueKey}-override-input`}
              className={`h-9 ${isModified(valueKey) ? 'border-orange-400 bg-orange-50/30' : ''} ${!canEdit ? 'bg-muted cursor-not-allowed' : ''}`}
            />
          )}

          {drawnValue !== undefined && drawnValue !== null && (
            <p className="text-[11px] text-emerald-600 font-medium" data-testid={`shirt-${section}-${valueKey}-drawn-text`}>
              Drawn: {Number(drawnValue).toFixed(3)}"
            </p>
          )}
        </div>
      );
    };

    const renderAdvancedPanel = () => {
      if (!activeAdvancedControl) return null;

      const panelMap = {
        neck: {
          title: 'Neck Advanced Controls',
          content: (
            <div className="space-y-3">
              {renderOverrideControl({
                section: 'neck',
                label: 'Front Neck Width (FNW)',
                enabledKey: 'overrideFNWEnabled',
                valueKey: 'overrideFNW',
                autoValue: matchingSummary.autoFrontNeckWidth,
                drawnValue: matchingSummary.resolvedFrontNeckWidth,
              })}
              {renderOverrideControl({
                section: 'neck',
                label: 'Back Neck Width (BNW)',
                enabledKey: 'overrideBNWEnabled',
                valueKey: 'overrideBNW',
                autoValue: matchingSummary.autoBackNeckWidth,
                drawnValue: matchingSummary.resolvedBackNeckWidth,
              })}
              {renderOverrideControl({
                section: 'neck',
                label: 'Front Neck Depth (FND)',
                enabledKey: 'overrideFNDEnabled',
                valueKey: 'overrideFND',
                autoValue: matchingSummary.autoFrontNeckDepth,
                drawnValue: matchingSummary.resolvedFrontNeckDepth,
              })}
              {renderOverrideControl({
                section: 'neck',
                label: 'Back Neck Depth (BND)',
                enabledKey: 'overrideBNDEnabled',
                valueKey: 'overrideBND',
                autoValue: matchingSummary.autoBackNeckDepth,
                drawnValue: matchingSummary.resolvedBackNeckDepth,
              })}
            </div>
          ),
        },
        shoulder: {
          title: 'Shoulder Advanced Controls',
          content: (
            <div className="space-y-3">
              {renderShirtInput('backShoulderSlope', 'Back Shoulder Slope', measurements.backShoulderSlope)}
              {renderShirtInput('frontShoulderSlope', 'Front Shoulder Slope', measurements.frontShoulderSlope)}
              {renderShirtInput('backToFtHps', 'Bk to FT HPS', measurements.backToFtHps)}
              <div className="rounded-md border bg-muted/30 px-3 py-2 space-y-1" data-testid="shirt-shoulder-angle-summary-panel">
                <p className="text-[11px] font-medium text-foreground" data-testid="shirt-back-shoulder-angle-panel-text">
                  Back shoulder angle: {Number(matchingSummary?.backShoulderAngleDegrees || 0).toFixed(2)}°
                </p>
                <p className="text-[11px] font-medium text-foreground" data-testid="shirt-front-shoulder-angle-panel-text">
                  Front shoulder angle: {Number(matchingSummary?.frontShoulderAngleDegrees || 0).toFixed(2)}°
                </p>
                <p className="text-[11px] text-muted-foreground" data-testid="shirt-shoulder-angle-panel-note-text">
                  Angle is shown against the horizontal and updates live while you edit shoulder slope.
                </p>
              </div>
              <p className="text-[11px] text-muted-foreground" data-testid="shirt-shoulder-advanced-default-note">
                Defaults: Back Shoulder Slope = 2", Front Shoulder Slope = 2", Bk to FT HPS = 1".
              </p>
            </div>
          ),
        },
        chest: {
          title: 'Chest Advanced Controls',
          content: (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Front Chest</Label>
                <Input
                  type="number"
                  step="0.125"
                  {...getBufferedInputHandlers('frontChestResolved', frontChestResolved.toFixed(3), {
                    commitKey: 'frontChestResolved',
                  })}
                  disabled={!canEdit}
                  data-testid="shirt-front-chest-resolved-input"
                  className={`h-9 ${isModified('frontChestSplit') ? 'border-orange-400 bg-orange-50/30' : ''} ${!canEdit ? 'bg-muted cursor-not-allowed' : ''}`}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Back Chest</Label>
                <Input
                  type="number"
                  step="0.125"
                  value={backChestResolved.toFixed(3)}
                  readOnly
                  disabled
                  data-testid="shirt-back-chest-resolved-input"
                  className="h-9 bg-muted cursor-not-allowed"
                />
              </div>
              <p className="text-[11px] text-muted-foreground" data-testid="shirt-chest-split-note-text">
                Front Chest is editable. Back Chest auto-balances to keep total chest constant.
              </p>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => onAutoShirtSideSeam?.()}
                disabled={!canEdit}
                data-testid="shirt-chest-auto-side-seam-button"
              >
                Auto Side Seam
              </Button>
              <p className="text-[11px] text-muted-foreground" data-testid="shirt-chest-auto-side-seam-note-text">
                Quick access: re-solves chest→waist→belly→seat side seam controls using current values.
              </p>
              {renderSideSeamSegmentsInfo()}
            </div>
          ),
        },
        armhole: {
          title: 'Armhole Advanced Controls',
          content: (
            <div className="space-y-3">
              {renderShirtInput('armholeScyeDepth', 'Armhole Scye Depth', measurements.armholeScyeDepth)}
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => onAutoShirtArmholeCurves?.()}
                disabled={!canEdit}
                data-testid="shirt-armhole-auto-curves-button"
              >
                Auto Armhole Curves
              </Button>
              <p className="text-[11px] text-muted-foreground" data-testid="shirt-armhole-auto-curves-note-text">
                Re-solves front/back body armhole curve controls using constrained tangents (horizontal at shoulder, vertical at side seam). Manual curve edits remain available.
              </p>
            </div>
          ),
        },
        elbow: {
          title: 'Elbow Advanced Controls',
          content: (
            <div className="space-y-3">
              {renderShirtInput(
                'sleeveDistToElbow',
                'Distance to Elbow (SL1 → Elbow)',
                measurements?.sleeveDistToElbow || matchingSummary?.sleeveDistToElbow || 0,
              )}
              <p className="text-[11px] text-muted-foreground" data-testid="shirt-elbow-advanced-note-text">
                Auto-computed from sleeve geometry; editable for long sleeve as advanced control.
              </p>
            </div>
          ),
        },
        shirtLength: {
          title: 'Shirt Length Advanced Controls',
          content: (
            <div className="space-y-3">
              {renderShirtInput('tailDepthBack', 'Back Tail Depth', measurements.tailDepthBack ?? measurements.tailDepth ?? 2)}
              {renderShirtInput('tailDepthFront', 'Front Tail Depth', measurements.tailDepthFront ?? measurements.tailDepth ?? 2)}
              {renderShirtInput('shirtLengthFront', 'Shirt Length Front (FHPS→CF7)', measurements.shirtLengthFront ?? matchingSummary.shirtLengthFrontAuto)}
              <p className="text-[11px] text-muted-foreground" data-testid="shirt-length-advanced-note">
                Default front length = back length − Bk to FT HPS. Tail depths tune CF6→CF7 and CB6→CB7 separately (defaults 2").
              </p>
            </div>
          ),
        },
        sleeve: {
          title: 'Sleeve Advanced Controls',
          content: (
            <div className="space-y-3">
              {measurements.sleeveStyle !== 'short' && (
                <div className={DENSE_FIELD_STACK_CLASS}>
                  <div className={DENSE_FIELD_ROW_CLASS}>
                    <Label className={DENSE_LABEL_CLASS}>Cuff Width</Label>
                  </div>
                  <Input
                    type="number"
                    step="0.125"
                    {...getBufferedInputHandlers('cuffWidth', measurements.cuffWidth ?? 2.5)}
                    disabled={!canEdit}
                    data-testid="shirt-advanced-cuffWidth-input"
                    className={`${DENSE_INPUT_CLASS} ${isModified('cuffWidth') ? 'border-orange-400 bg-orange-50/30' : ''} ${!canEdit ? 'bg-muted cursor-not-allowed' : ''}`}
                  />
                </div>
              )}
              <p className="text-[11px] text-muted-foreground" data-testid="shirt-sleeve-cuff-width-note-text">
                Total sleeve length stays fixed. Drafted sleeve body = total sleeve length minus cuff width.
              </p>
              {measurements.sleeveStyle !== 'short' && (
                <p className="text-[11px] text-emerald-700 font-medium" data-testid="shirt-sleeve-cuff-width-derived-text">
                  Body drafted: {Number(matchingSummary?.sleeveDraftLength || 0).toFixed(2)}&quot; = Total {Number(matchingSummary?.sleeveDisplayedLength || measurements?.sleeveLength || 0).toFixed(2)}&quot; - Cuff {Number(measurements?.cuffWidth ?? 2.5).toFixed(2)}&quot;
                </p>
              )}
            </div>
          ),
        },
        waist: {
          title: 'Waist Advanced Controls',
          content: (
            <div className="space-y-3">
              {renderShirtInput(
                'distanceToWaist',
                geometryVersionNormalized === 'shirt_geom_v4' ? 'CB1 → waist line' : 'Distance to Waist',
                measurements.distanceToWaist,
              )}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Front Waist (net half)</Label>
                <Input
                  type="number"
                  step="0.125"
                  {...getBufferedInputHandlers('frontWaistResolvedShirt', frontWaistResolvedShirt.toFixed(3), {
                    commitKey: 'frontWaistResolvedShirt',
                  })}
                  disabled={!canEdit}
                  data-testid="shirt-front-waist-resolved-input"
                  className={`h-9 ${isModified('frontWaistSplit') ? 'border-orange-400 bg-orange-50/30' : ''} ${!canEdit ? 'bg-muted cursor-not-allowed' : ''}`}
                />
                {isLadiesShirt && Number(matchingSummary?.frontWaistDartIntake || 0) > 0 && (
                  <p className="text-[10px] text-muted-foreground" data-testid="shirt-front-waist-dart-note">
                    Raw CF4→FWP {Number(matchingSummary.frontWaistLine || 0).toFixed(3)}&quot; − fish dart {Number(matchingSummary.frontWaistDartIntake || 0).toFixed(3)}&quot; = net {Number(matchingSummary.frontWaistNetLine || 0).toFixed(3)}&quot;
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Back Waist (net half)</Label>
                <Input
                  type="number"
                  step="0.125"
                  value={backWaistResolvedShirt.toFixed(3)}
                  readOnly
                  disabled
                  data-testid="shirt-back-waist-resolved-input"
                  className="h-9 bg-muted cursor-not-allowed"
                />
              </div>
              <div className="space-y-1.5" data-testid="shirt-side-seam-auto-depth-input-wrap">
                <Label className="text-xs text-muted-foreground">Auto Side Seam Depth (Chest→Waist)</Label>
                <Input
                  type="number"
                  step="0.05"
                  {...getBufferedInputHandlers('sideSeamAutoDepth', measurements.sideSeamAutoDepth ?? 1.625, {
                    commitKey: 'sideSeamAutoDepth',
                  })}
                  disabled={!canEdit}
                  data-testid="shirt-side-seam-auto-depth-input"
                  className={`h-9 ${isModified('sideSeamAutoDepth') ? 'border-orange-400 bg-orange-50/30' : ''} ${!canEdit ? 'bg-muted cursor-not-allowed' : ''}`}
                />
                <p className="text-[11px] text-muted-foreground" data-testid="shirt-side-seam-auto-depth-note-text">
                  1.00 = neutral auto fairing. Higher values deepen inward chest→waist curvature.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => onAutoShirtSideSeam?.()}
                disabled={!canEdit}
                data-testid="shirt-waist-auto-side-seam-button"
              >
                Auto Side Seam
              </Button>
              <p className="text-[11px] text-muted-foreground" data-testid="shirt-waist-advanced-note">
                {geometryVersionNormalized === 'shirt_geom_v4'
                  ? 'V4: waist level is CB1.y + this distance (same Y on front and back).'
                  : 'Controls vertical placement of waist level from upper body reference (FHPS).'}
              </p>
              <p className="text-[11px] text-muted-foreground" data-testid="shirt-waist-auto-side-seam-note-text">
                Auto Side Seam computes dX/dY for all 3 front + 3 back side-seam segments. You can still fine-tune manually in Curve Controls.
              </p>
              {renderSideSeamSegmentsInfo()}
            </div>
          ),
        },
        belly: {
          title: 'Belly Advanced Controls',
          content: (
            <div className="space-y-3">
              {renderShirtInput(
                'distanceToBelly',
                geometryVersionNormalized === 'shirt_geom_v4' ? 'Waist → belly drop' : 'Distance to Belly',
                measurements.distanceToBelly,
              )}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Front Belly (net half)</Label>
                <Input
                  type="number"
                  step="0.125"
                  {...getBufferedInputHandlers('frontBellyResolvedShirt', frontBellyResolvedShirt.toFixed(3), {
                    commitKey: 'frontBellyResolvedShirt',
                  })}
                  disabled={!canEdit}
                  data-testid="shirt-front-belly-resolved-input"
                  className={`h-9 ${isModified('frontBellySplit') ? 'border-orange-400 bg-orange-50/30' : ''} ${!canEdit ? 'bg-muted cursor-not-allowed' : ''}`}
                />
                {isLadiesShirt && Number(matchingSummary?.frontBellyDartIntake || 0) > 0 && (
                  <p className="text-[10px] text-muted-foreground" data-testid="shirt-front-belly-dart-note">
                    Raw CF5→FBP {Number(matchingSummary.frontBellyLine || 0).toFixed(3)}&quot; − fish dart {Number(matchingSummary.frontBellyDartIntake || 0).toFixed(3)}&quot; = net {Number(matchingSummary.frontBellyNetLine || 0).toFixed(3)}&quot;
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Back Belly (net half)</Label>
                <Input
                  type="number"
                  step="0.125"
                  value={backBellyResolvedShirt.toFixed(3)}
                  readOnly
                  disabled
                  data-testid="shirt-back-belly-resolved-input"
                  className="h-9 bg-muted cursor-not-allowed"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => onAutoShirtSideSeam?.()}
                disabled={!canEdit}
                data-testid="shirt-belly-auto-side-seam-button"
              >
                Auto Side Seam
              </Button>
              <p className="text-[11px] text-muted-foreground" data-testid="shirt-belly-advanced-note">
                Controls vertical placement of belly level from upper body reference.
              </p>
              <p className="text-[11px] text-muted-foreground" data-testid="shirt-belly-auto-side-seam-note-text">
                Use this to regenerate smooth chest→waist→belly→seat side seam first, then apply manual curve edits only if needed.
              </p>
              <p className="text-[11px] text-muted-foreground" data-testid="shirt-belly-auto-side-seam-depth-ref-note-text">
                Chest→waist inward depth is controlled from Waist Advanced Controls.
              </p>
              {renderSideSeamSegmentsInfo()}
            </div>
          ),
        },
        seat: {
          title: 'Hem/Seat Advanced Controls',
          content: (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Front Hem (Seat)</Label>
                <Input
                  type="number"
                  step="0.125"
                  {...getBufferedInputHandlers('frontSeatResolvedShirt', frontSeatResolvedShirt.toFixed(3), {
                    commitKey: 'frontSeatResolvedShirt',
                  })}
                  disabled={!canEdit}
                  data-testid="shirt-front-seat-resolved-input"
                  className={`h-9 ${isModified('frontSeatSplit') ? 'border-orange-400 bg-orange-50/30' : ''} ${!canEdit ? 'bg-muted cursor-not-allowed' : ''}`}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Back Hem (Seat)</Label>
                <Input
                  type="number"
                  step="0.125"
                  value={backSeatResolvedShirt.toFixed(3)}
                  readOnly
                  disabled
                  data-testid="shirt-back-seat-resolved-input"
                  className="h-9 bg-muted cursor-not-allowed"
                />
              </div>
              <p className="text-[11px] text-muted-foreground" data-testid="shirt-seat-split-note-text">
                Front Hem/Seat is editable. Back auto-balances so total hem/seat remains constant.
              </p>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => onAutoShirtSideSeam?.()}
                disabled={!canEdit}
                data-testid="shirt-seat-auto-side-seam-button"
              >
                Auto Side Seam
              </Button>
              <p className="text-[11px] text-muted-foreground" data-testid="shirt-seat-auto-side-seam-note-text">
                Seat shortcut: run Auto Side Seam here before refining any seat/hem curve controls.
              </p>
              {renderSideSeamSegmentsInfo()}
            </div>
          ),
        },
        acrossBack: {
          title: 'Across Back Advanced Controls',
          content: (
            <div className="space-y-3">
              {renderOverrideControl({
                section: 'across-back',
                label: 'Vertical Distance from CB1 to CB2',
                enabledKey: 'overrideAcrossBackDepthEnabled',
                valueKey: 'overrideAcrossBackDepth',
                autoValue: matchingSummary.autoAcrossBackDepth,
                drawnValue: matchingSummary.resolvedAcrossBackDepth,
              })}
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => onAutoShirtBackYokeDartCurve?.()}
                disabled={!canEdit || !isYokeDartActive}
                data-testid="shirt-across-back-auto-yoke-dart-curve-button"
              >
                Auto Back Yoke Dart Curve
              </Button>
              <p className="text-[11px] text-muted-foreground" data-testid="shirt-across-back-auto-yoke-dart-note-text">
                Re-solves back-panel yoke dart curve offsets from current geometry (CB1→CB2 changes included) while keeping manual edit available.
              </p>
            </div>
          ),
        },
        acrossFront: {
          title: 'Across Front Advanced Controls',
          content: renderOverrideControl({
            section: 'across-front',
            label: 'Vertical Distance from CF1 to CF2',
            enabledKey: 'overrideAcrossFrontDepthEnabled',
            valueKey: 'overrideAcrossFrontDepth',
            autoValue: matchingSummary.autoAcrossFrontDepth,
            drawnValue: matchingSummary.resolvedAcrossFrontDepth,
          }),
        },
      };

      const panel = panelMap[activeAdvancedControl];
      if (!panel) return null;

      return (
        <AdvancedOverlayPanel
          title={panel.title}
          onClose={() => setActiveAdvancedControl(null)}
          testIdPrefix="shirt-advanced-controls-side-panel"
        >
          {panel.content}
        </AdvancedOverlayPanel>
      );
    };

    const pointReferences = patternData?.pointReferences || [];

    return (
      <aside className={`w-[392px] flex-shrink-0 border-r bg-card h-full flex flex-col shadow-lg ${!canEdit ? 'pointer-events-none select-none' : ''}`} data-testid="left-panel-shirt-garment">
        <div className="p-3 border-b bg-muted/10">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <Settings size={14} /> Shirt Base Block (SIy v1)
          </h2>
          {!canEdit && (
            <p className="text-xs text-amber-700 mt-2 font-semibold" data-testid="left-panel-shirt-locked-text">LOCKED — Unlock to edit</p>
          )}
        </div>

        <ScrollArea className="flex-1">
          <div className="p-2 flex gap-2">
            <IconTabRail
              tabs={shirtPanelTabs}
              activeTab={activeShirtPanel}
              onTabChange={setActiveShirtPanel}
              testId="shirt-panel-icon-rail"
            />

            <div className="flex-1 min-w-0">
            <Accordion type="single" collapsible value={activeShirtPanel} onValueChange={(value) => value && setActiveShirtPanel(value)} className="space-y-0">
              <AccordionItem value="shirt-body" className={activeShirtPanel === 'shirt-body' ? 'border rounded-md bg-card px-2' : 'hidden'} data-testid="shirt-body-accordion-item">
                <AccordionTrigger className="hidden">
                  <div className="flex items-center gap-2">
                    <Ruler size={16} className="text-primary" />
                    <span className="font-semibold text-sm">Body Inputs</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-1.5 pb-2 space-y-1.5">
                  {renderGeometryEditingPanel('shirt-geometry-edit-state-panel', {
                    cadDense: true,
                    showFreePointHelp: true,
                    freePointGarmentLabel: 'shirt',
                    selectedPointCount: selectedFreePointsCount,
                  })}

                  {renderDrawnMeasurementPanel(shirtDrawnRows, 'shirt-drawn-measurements-panel', {
                    cadDense: true,
                    hideSeed: true,
                    hideHelperText: true,
                  })}

                  <div className="space-y-1" data-testid="shirt-body-style-select-wrap">
                    <Label className="text-[8px] font-medium uppercase tracking-[0.16em] text-slate-500">Shirt block</Label>
                    <Select
                      value={String(measurements?.shirtBodyStyle || 'gents')}
                      onValueChange={(value) => onMeasurementChange('shirtBodyStyle', value)}
                      disabled={!canEdit}
                    >
                      <SelectTrigger className={DENSE_SELECT_TRIGGER_CLASS} data-testid="shirt-body-style-select-trigger">
                        <SelectValue placeholder="Gents or Ladies" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gents" data-testid="shirt-body-style-gents">Gents</SelectItem>
                        <SelectItem value="ladies" data-testid="shirt-body-style-ladies">Ladies</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1" data-testid="shirt-portly-select-wrap">
                    <Label className="text-[8px] font-medium uppercase tracking-[0.16em] text-slate-500">Portly</Label>
                    <Select
                      value={String(measurements?.portlyType || 'normal')}
                      onValueChange={(value) => onMeasurementChange('portlyType', value)}
                      disabled={!canEdit}
                    >
                      <SelectTrigger className={DENSE_SELECT_TRIGGER_CLASS} data-testid="shirt-portly-select-trigger">
                        <SelectValue placeholder="Select portly type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="thin" data-testid="shirt-portly-option-thin">Thin</SelectItem>
                        <SelectItem value="normal" data-testid="shirt-portly-option-normal">Normal</SelectItem>
                        <SelectItem value="slight_belly" data-testid="shirt-portly-option-slight-belly">Slight Belly</SelectItem>
                        <SelectItem value="big_belly" data-testid="shirt-portly-option-big-belly">Big Belly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1" data-testid="shirt-hem-mode-select-wrap">
                    <Label className="text-[8px] font-medium uppercase tracking-[0.16em] text-slate-500">Hem Curve Mode</Label>
                    <Select
                      value={String(Number(measurements.hemCurveMode ?? 1))}
                      onValueChange={(value) => onMeasurementChange('hemCurveMode', value)}
                      disabled={!canEdit}
                    >
                      <SelectTrigger className={DENSE_SELECT_TRIGGER_CLASS} data-testid="shirt-hem-curve-mode-select-trigger">
                        <SelectValue placeholder="Select hem mode" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1" data-testid="shirt-hem-curve-mode-s-curve-option">S-Curve</SelectItem>
                        <SelectItem value="0" data-testid="shirt-hem-curve-mode-classic-option">Classic</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="shirt-dart" className={activeShirtPanel === 'shirt-dart' ? 'border rounded-lg bg-card px-3' : 'hidden'} data-testid="shirt-dart-accordion-item">
                <AccordionTrigger className="hidden">
                  <div className="flex items-center gap-2">
                    <Settings size={16} className="text-primary" />
                    <span className="font-semibold text-sm">Fish Dart Inputs</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2 pb-4">
                  <div className="flex items-center justify-between p-2 rounded-md border bg-muted/20 mb-3" data-testid="shirt-fish-dart-enabled-toggle-row">
                    <span className="text-sm font-medium">Enable Fish Dart</span>
                    <Switch
                      checked={isFishDartEnabled}
                      onCheckedChange={(checked) => onMeasurementChange('fishDartEnabled', checked)}
                      data-testid="shirt-fish-dart-enabled-toggle-switch"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {renderShirtInput('dartWidthWaist', 'Dart Width at Waist', measurements.dartWidthWaist, { readOnly: !isFishDartEnabled })}
                    {renderShirtInput('dartWidthBelly', 'Dart Width at Belly', measurements.dartWidthBelly, { readOnly: !isFishDartEnabled })}
                    {renderShirtInput('dartLengthTop', 'Dart Length Top', measurements.dartLengthTop, { readOnly: !isFishDartEnabled })}
                    {renderShirtInput('dartLengthBottom', 'Dart Length Bottom', measurements.dartLengthBottom, { readOnly: !isFishDartEnabled })}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-3" data-testid="shirt-dart-note-text">
                    {isFishDartEnabled
                      ? 'Dart suppression is drafted separately and does not alter front/back width split equations.'
                      : 'Fish Dart is OFF. Back dart suppression geometry is excluded from drafting.'}
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="shirt-bust-dart" className={activeShirtPanel === 'shirt-bust-dart' ? 'border rounded-lg bg-card px-3' : 'hidden'} data-testid="shirt-bust-dart-accordion-item">
                <AccordionTrigger className="hidden"><span>Bust Dart</span></AccordionTrigger>
                <AccordionContent className="pt-2 pb-4 space-y-3">
                  <div className="flex items-center justify-between p-2 rounded-md border bg-muted/20" data-testid="shirt-bust-dart-enabled-toggle-row">
                    <span className="text-sm font-medium">Enable bust dart</span>
                    <Switch
                      checked={measurements?.bustDartEnabled === true}
                      onCheckedChange={(checked) => onMeasurementChange('bustDartEnabled', checked)}
                      data-testid="shirt-bust-dart-enabled-toggle-switch"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {renderShirtInput('bustDartLeadFromChest', 'BDA1 from FCP (along seam)', measurements.bustDartLeadFromChest ?? 2)}
                    {renderShirtInput('bustDartOpeningLength', 'BDA1→BDA2 opening', measurements.bustDartOpeningLength ?? measurements.bustDartWidth ?? 1)}
                    {renderShirtInput('bustDartApexFromSide', 'BDA3 toward CF', measurements.bustDartApexFromSide ?? 4)}
                  </div>
                  {measurements?.bustDartEnabled === true && (
                    <div className="rounded-md border bg-muted/20 p-2 space-y-1 text-[11px]" data-testid="shirt-bust-dart-summary">
                      <p>
                        Bust target full: <span className="font-semibold">{Number(matchingSummary?.targetBustFull || measurements?.bust || 0).toFixed(3)}&quot;</span>
                        {' · '}
                        drawn half at dart: <span className="font-semibold">{Number(matchingSummary?.bustHalfDrawn || 0).toFixed(3)}&quot;</span>
                        {' · '}
                        bust spread at BDA: <span className="font-semibold">{Number(matchingSummary?.bustSpreadApplied || 0).toFixed(3)}&quot;</span>
                      </p>
                      <p>
                        Side-seam extension (lower panel drop): <span className="font-semibold">{Number(matchingSummary?.sideSeamExtensionApplied || 0).toFixed(3)}&quot;</span>
                        {' · '}
                        dart intake BDA1–BDA2: <span className="font-semibold">{Number(matchingSummary?.bustDartIntakeDrawn || measurements?.bustDartOpeningLength || 0).toFixed(3)}&quot;</span>
                      </p>
                    </div>
                  )}
                  {['BDA1', 'BDA2', 'BDA3'].map((pointName) => {
                    const point = (patternData?.namedPoints || []).find((entry) => entry.name === pointName && entry.piece === 'front');
                    if (!point) return null;
                    const draftY = Number(-point.y).toFixed(3);
                    return (
                      <p key={pointName} className="text-[11px] text-muted-foreground font-mono" data-testid={`shirt-bust-dart-point-${pointName}`}>
                        {pointName}: x {Number(point.x).toFixed(3)}&quot;, y {draftY}&quot;
                      </p>
                    );
                  })}
                  <p className="text-[10px] text-muted-foreground">
                    Side seam: FCP→BDA1→BDA3→BDA2→FWP. Dart fold lines BDA1–BDA3–BDA2. Net chest→waist side = FCP→BDA1 + BDA2→FWP after the dart is sewn.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="shirt-front-fish-dart" className={activeShirtPanel === 'shirt-front-fish-dart' ? 'border rounded-lg bg-card px-3' : 'hidden'} data-testid="shirt-front-fish-dart-accordion-item">
                <AccordionTrigger className="hidden"><span>Front Fish Dart</span></AccordionTrigger>
                <AccordionContent className="pt-2 pb-4 space-y-3">
                  <div className="flex items-center justify-between p-2 rounded-md border bg-muted/20" data-testid="shirt-front-fish-dart-enabled-toggle-row">
                    <span className="text-sm font-medium">Enable front fish dart</span>
                    <Switch
                      checked={measurements?.frontFishDartEnabled !== false}
                      onCheckedChange={(checked) => onMeasurementChange('frontFishDartEnabled', checked)}
                      data-testid="shirt-front-fish-dart-enabled-toggle-switch"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {renderShirtInput('frontDartWidthWaist', 'Waist width', measurements.frontDartWidthWaist)}
                    {renderShirtInput('frontDartWidthBelly', 'Belly width', measurements.frontDartWidthBelly)}
                    {renderShirtInput('frontDartLengthTop', 'Length top', measurements.frontDartLengthTop)}
                    {renderShirtInput('frontDartLengthBottom', 'Length bottom', measurements.frontDartLengthBottom)}
                  </div>
                  {['FD0', 'FD1', 'FD2', 'FD3'].map((pointName) => {
                    const point = (patternData?.namedPoints || []).find((entry) => entry.name === pointName && entry.piece === 'front');
                    if (!point) return null;
                    const draftY = Number(-point.y).toFixed(3);
                    return (
                      <p key={pointName} className="text-[11px] text-muted-foreground font-mono" data-testid={`shirt-front-fish-dart-point-${pointName}`}>
                        {pointName}: x {Number(point.x).toFixed(3)}&quot;, y {draftY}&quot;
                      </p>
                    );
                  })}
                  <p className="text-[10px] text-muted-foreground">
                    Hover or click FD points on the front panel for coordinates. FD0 = top apex; FD1/FD2 = waist legs; FD3 = belly leg (left).
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="shirt-back-style" className={activeShirtPanel === 'shirt-back-style' ? 'border rounded-lg bg-card px-3' : 'hidden'} data-testid="shirt-back-style-accordion-item">
                <AccordionTrigger className="hidden">
                  <div className="flex items-center gap-2">
                    <Settings size={16} className="text-primary" />
                    <span className="font-semibold text-sm">Back Style</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2 pb-4 space-y-3">
                  <div className="space-y-1.5" data-testid="shirt-back-style-select-wrap">
                    <Label className="text-xs text-muted-foreground">Back Style Option</Label>
                    <Select
                      value={backStyle}
                      onValueChange={(value) => onMeasurementChange('backStyle', value)}
                      disabled={!canEdit}
                    >
                      <SelectTrigger className="h-9" data-testid="shirt-back-style-select-trigger">
                        <SelectValue placeholder="Select back style" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none" data-testid="shirt-back-style-none-option">None (Default)</SelectItem>
                        <SelectItem value="side_pleat" data-testid="shirt-back-style-side-pleat-option">Side Pleat</SelectItem>
                        <SelectItem value="center_pleat" data-testid="shirt-back-style-center-pleat-option">Center Pleat</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {backStyle === 'side_pleat' && (
                    <div className="grid grid-cols-2 gap-3" data-testid="shirt-side-pleat-input-grid">
                      {renderShirtInput('backPleatWidth', 'Side Pleat Width', measurements.backPleatWidth)}
                      {renderShirtInput('backPleatLength', 'Side Pleat Length', measurements.backPleatLength)}
                      {renderShirtInput(
                        'backSidePleatOffsetFromBYDB',
                        'Side Pleat Position from BYDB',
                        Number(measurements?.backSidePleatOffsetFromBYDB || 0) > 0
                          ? measurements.backSidePleatOffsetFromBYDB
                          : Number(matchingSummary?.backSidePleatAutoOffsetFromBYDB || 0),
                      )}
                      <div className="col-span-2 text-[11px] text-muted-foreground" data-testid="shirt-side-pleat-auto-note-text">
                        Auto position = 20% of CBY→BYDB path (rounded to 0.125"). Set to 0 to keep auto.
                      </div>
                      <div className="col-span-2 text-[11px] text-muted-foreground" data-testid="shirt-side-pleat-resolved-offset-text">
                        Resolved BYDB→SP1 distance: {Number(matchingSummary?.backSidePleatResolvedFromBYDB || matchingSummary?.backSidePleatAutoOffsetFromBYDB || 0).toFixed(3)}"
                      </div>
                      {!matchingSummary?.backSidePleatApplied && (
                        <div className="col-span-2 text-[11px] text-amber-700" data-testid="shirt-side-pleat-availability-note-text">
                          Side Pleat drafting requires Yoke enabled with active Yoke Dart split points (BYM/BYDB).
                        </div>
                      )}
                    </div>
                  )}

                  {backStyle === 'center_pleat' && (
                    <div className="grid grid-cols-2 gap-3" data-testid="shirt-center-pleat-input-grid">
                      {renderShirtInput('backPleatWidth', 'Center Pleat Width', measurements.backPleatWidth)}
                      {renderShirtInput('backPleatLength', 'Center Pleat Length', measurements.backPleatLength)}
                      <div className="col-span-2 text-[11px] text-muted-foreground" data-testid="shirt-center-pleat-note-text">
                        Center Pleat uses side-pleat shift logic. CB seam is treated as pleat leg-1, so only one marker line is drafted on CBY→BYM at Pleat Width.
                      </div>
                      <div className="col-span-2 text-[11px] text-muted-foreground" data-testid="shirt-center-pleat-resolved-offset-text">
                        Resolved CBY→Marker distance: {Number(matchingSummary?.backCenterPleatMarkerFromCBY || 0).toFixed(3)}"
                      </div>
                      {!matchingSummary?.backCenterPleatApplied && (
                        <div className="col-span-2 text-[11px] text-amber-700" data-testid="shirt-center-pleat-availability-note-text">
                          Center Pleat drafting requires Yoke enabled with active Yoke Dart split points (BYM/BYDB).
                        </div>
                      )}
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="shirt-yoke" className={activeShirtPanel === 'shirt-yoke' ? 'border rounded-lg bg-card px-3' : 'hidden'} data-testid="shirt-yoke-accordion-item">
                <AccordionTrigger className="hidden">
                  <div className="flex items-center justify-between w-full pr-2">
                    <div className="flex items-center gap-2">
                      <Settings size={16} className="text-primary" />
                      <span className="font-semibold text-sm">Yoke Inputs</span>
                    </div>
                    <span
                      className={`text-[10px] font-semibold rounded-full px-2 py-1 border ${isYokeDartActive ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-slate-100 text-slate-600 border-slate-300'}`}
                      data-testid="shirt-yoke-dart-state-chip"
                    >
                      {isYokeDartActive ? 'DART ACTIVE' : 'DART OFF'}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2 pb-4">
                  <div className="flex items-center justify-between p-2 rounded-md border bg-muted/20 mb-3" data-testid="shirt-yoke-enabled-toggle-row">
                    <span className="text-sm font-medium">Enable Yoke</span>
                    <Switch
                      checked={isYokeEnabled}
                      onCheckedChange={(checked) => onMeasurementChange('yokeEnabled', checked)}
                      data-testid="shirt-yoke-enabled-toggle-switch"
                    />
                  </div>

                  {isYokeEnabled && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        {renderShirtInput('yokeDepth', 'Yoke Depth', measurements.yokeDepth)}
                        {renderShirtInput('frontNeckRollover', 'Front Neck Rollover', measurements.frontNeckRollover)}
                        {renderShirtInput('frontArmholeRollover', 'Front Armhole Rollover', measurements.frontArmholeRollover)}
                        {renderShirtInput('yokeDartWidth', 'Yoke Dart Width', measurements.yokeDartWidth)}
                        {renderShirtInput('yokeDartLength', 'Yoke Dart Length', measurements.yokeDartLength)}
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        onClick={() => onAutoShirtYokePanelDartCurve?.()}
                        disabled={!canEdit || !isYokeDartActive}
                        data-testid="shirt-yoke-auto-yoke-panel-dart-curve-button"
                      >
                        Auto Yoke Panel Dart Curve
                      </Button>
                      <p className="text-[11px] text-muted-foreground" data-testid="shirt-yoke-auto-yoke-panel-dart-note-text">
                        Re-solves yoke-panel dart curve offsets from current yoke geometry; manual curve control remains available if further tuning is needed.
                      </p>
                    </div>
                  )}

                  {!isYokeEnabled && (
                    <p className="text-xs text-muted-foreground" data-testid="shirt-yoke-disabled-note-text">
                      Yoke is disabled. Drafting now uses only Front + Back panels.
                    </p>
                  )}

                  <p className="text-[11px] text-muted-foreground mt-3" data-testid="shirt-yoke-note-text">
                    Yoke cut rules: depth is measured down from CB1. Front rollover points are measured along front neck and armhole curves, then joined by a straight cut line.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="shirt-sleeve" className={activeShirtPanel === 'shirt-sleeve' ? 'border rounded-lg bg-card px-3' : 'hidden'} data-testid="shirt-sleeve-accordion-item">
                <AccordionTrigger className="hidden">
                  <div className="flex items-center gap-2">
                    <Settings size={16} className="text-primary" />
                    <span className="font-semibold text-sm">Sleeve Inputs</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2 pb-4 space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Sleeve Style</Label>
                    <Select
                      value={measurements.sleeveStyle === 'short' ? 'short' : 'long'}
                      onValueChange={(value) => onMeasurementChange('sleeveStyle', value)}
                      disabled={!canEdit}
                    >
                      <SelectTrigger data-testid="shirt-sleeve-style-select-trigger">
                        <SelectValue placeholder="Select sleeve style" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="long" data-testid="shirt-sleeve-style-long-option">Long Sleeve</SelectItem>
                        <SelectItem value="short" data-testid="shirt-sleeve-style-short-option">Short Sleeve</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {renderShirtInput('bicepWidth', 'Bicep Width (Unwrapped)', measurements.bicepWidth)}
                    {measurements.sleeveStyle !== 'short'
                  ? renderShirtInput('elbowWidth', 'Elbow Width (Unwrapped)', measurements.elbowWidth, { advancedKey: 'elbow' })
                      : renderShirtInput('shortSleeveHem', 'Short Sleeve Hem (Unwrapped)', measurements.shortSleeveHem)}
                    {measurements.sleeveStyle !== 'short'
                      ? renderShirtInput('maxCuffWidth', 'Long Sleeve Cuff Width (Unwrapped)', measurements.maxCuffWidth)
                      : renderShirtInput('shortSleeveLength', 'Short Sleeve Length', measurements.shortSleeveLength)}
                    {measurements.sleeveStyle !== 'short' && renderShirtInput('sleeveLength', 'Long Sleeve Length', measurements.sleeveLength)}
                    {measurements.sleeveStyle !== 'short' && renderShirtInput('cuffWidth', 'Cuff Width', measurements.cuffWidth ?? 2.5, { advancedKey: 'sleeve' })}
                  </div>

                  <div className="space-y-2 rounded-md border bg-muted/20 p-3" data-testid="shirt-sleeve-front-pitch-slider-wrap">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Front Pitch Height % of Cap</span>
                      <span className="font-semibold" data-testid="shirt-sleeve-front-pitch-slider-value-text">
                        {(Number(measurements.sleeveFrontPitchPercent || 0.55) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <Slider
                      min={20}
                      max={60}
                      step={0.5}
                      value={[Number(measurements.sleeveFrontPitchPercent || 0.55) * 100]}
                      onValueChange={(values) => onMeasurementChange('sleeveFrontPitchPercent', (values[0] || 55) / 100)}
                      disabled={!canEdit}
                      data-testid="shirt-sleeve-front-pitch-slider"
                    />
                  </div>

                  <div className="space-y-2 rounded-md border bg-muted/20 p-3" data-testid="shirt-sleeve-back-pitch-slider-wrap">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Back Pitch Height % of Cap</span>
                      <span className="font-semibold" data-testid="shirt-sleeve-back-pitch-slider-value-text">
                        {(Number(measurements.sleeveBackPitchPercent || 0.45) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <Slider
                      min={30}
                      max={75}
                      step={0.5}
                      value={[Number(measurements.sleeveBackPitchPercent || 0.45) * 100]}
                      onValueChange={(values) => onMeasurementChange('sleeveBackPitchPercent', (values[0] || 45) / 100)}
                      disabled={!canEdit}
                      data-testid="shirt-sleeve-back-pitch-slider"
                    />
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => onAutoShirtSleeveCurvesFpBp?.()}
                    disabled={!canEdit}
                    data-testid="shirt-sleeve-auto-curves-fp-bp-button"
                  >
                    Auto Sleeve Curves (FP/BP)
                  </Button>
                  <p className="text-[11px] text-muted-foreground" data-testid="shirt-sleeve-auto-curves-fp-bp-note-text">
                    Re-solves sleeve curve offsets around FP and BP (front/back crown + underarm joins). Manual adjustments remain available.
                  </p>

                  <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-1" data-testid="shirt-sleeve-summary-box">
                    <p data-testid="shirt-sleeve-style-summary-text">Style: <span className="font-semibold uppercase">{(matchingSummary?.sleeveStyle || measurements.sleeveStyle || 'long')}</span></p>
                    <p data-testid="shirt-sleeve-cap-height-text">Solved cap height: <span className="font-semibold">{matchingSummary?.sleeveCapHeight ?? 0}"</span></p>
                    <p data-testid="shirt-sleeve-cap-path-text">Sleeve cap path: <span className="font-semibold">{matchingSummary?.sleeveCapPathLength ?? 0}"</span></p>
                    <p data-testid="shirt-sleeve-cap-target-text">Target (body armhole + ease): <span className="font-semibold">{matchingSummary?.sleeveCapTarget ?? 0}"</span></p>
                    <p data-testid="shirt-sleeve-bicep-drawn-width-text">Bicep drawn (unwrapped): <span className="font-semibold">{matchingSummary?.sleeveBicepUnwrappedDrawn ?? 0}"</span></p>
                    {measurements.sleeveStyle !== 'short' && (
                      <p data-testid="shirt-sleeve-elbow-drawn-width-text">Elbow drawn (unwrapped): <span className="font-semibold">{matchingSummary?.sleeveElbowUnwrappedDrawn ?? 0}"</span></p>
                    )}
                    {measurements.sleeveStyle !== 'short' && (
                      <p data-testid="shirt-sleeve-distance-to-elbow-text">Distance to elbow (SL1→Elbow): <span className="font-semibold">{matchingSummary?.sleeveDistToElbow ?? 0}"</span></p>
                    )}
                    <p data-testid="shirt-sleeve-hem-drawn-width-text">Hem drawn (unwrapped): <span className="font-semibold">{matchingSummary?.sleeveHemUnwrappedDrawn ?? 0}"</span></p>
                    <p data-testid="shirt-sleeve-side-seam-delta-text">Side seam delta (L-R): <span className="font-semibold">{matchingSummary?.sleeveSideSeamDelta ?? 0}"</span></p>
                  </div>

                  <p className="text-[11px] text-muted-foreground" data-testid="shirt-sleeve-note-text">
                    {measurements.sleeveStyle === 'short'
                      ? 'Short sleeve mode: no elbow feature, no sleeve placket, and no cuff deduction from sleeve length. Solver snaps sleeve draft coordinates to 1/16".'
                      : 'Long sleeve mode: width inputs are unwrapped values. Total sleeve length includes cuff width. Drafted sleeve body = total sleeve length - cuff width. Pleat allowance 0.75", placket height 4". Solver snaps sleeve draft coordinates to 1/16".'}
                  </p>
                </AccordionContent>
              </AccordionItem>

              {isShirtGeometryV3 && (
              <AccordionItem value="shirt-collar" className={activeShirtPanel === 'shirt-collar' ? 'border rounded-lg bg-card px-3' : 'hidden'} data-testid="shirt-collar-accordion-item">
                <AccordionTrigger className="hidden">
                  <div className="flex items-center gap-2">
                    <PanelTop size={16} className="text-primary" />
                    <span className="font-semibold text-sm">Collar</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2 pb-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    {renderShirtInput('collarBandBackHeight', 'Band Back Height', measurements.collarBandBackHeight ?? 1.25)}
                    {renderShirtInput('collarBandFrontHeight', 'Band Front Height', measurements.collarBandFrontHeight ?? 1)}
                    {renderShirtInput('collarBandFrontNeckPercent', 'Front Neck Trace %', measurements.collarBandFrontNeckPercent ?? 30, { step: '1' })}
                    {renderShirtInput('collarLeafBackHeight', 'Leaf Back Height', measurements.collarLeafBackHeight ?? 1.75)}
                    {renderShirtInput('collarLeafTieGap', 'Tie Gap', measurements.collarLeafTieGap ?? 1)}
                    {renderShirtInput('collarLeafPointLength', 'Point Length', measurements.collarLeafPointLength ?? 3)}
                    {renderShirtInput('collarLeafSpread', 'Spread', measurements.collarLeafSpread ?? 0)}
                  </div>

                  <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-1" data-testid="shirt-collar-band-summary-box">
                    <p data-testid="shirt-collar-band-length-text">
                      CbF0→CbB0: <span className="font-semibold">{Number(matchingSummary?.collarBandNeckLength || 0).toFixed(3)}&quot;</span>
                    </p>
                    <p data-testid="shirt-collar-band-target-text">
                      Half neck target: <span className="font-semibold">{Number(matchingSummary?.collarBandTargetHalfNeck || matchingSummary?.totalNeckHalf || 0).toFixed(3)}&quot;</span>
                      {Number.isFinite(Number(matchingSummary?.collarBandLengthError))
                        ? ` (Δ ${Number(matchingSummary.collarBandLengthError).toFixed(3)}")`
                        : ''}
                    </p>
                    <p data-testid="shirt-collar-band-trace-text">
                      Front neck traced: <span className="font-semibold">{Number(matchingSummary?.collarBandFrontTraceLength || 0).toFixed(3)}&quot;</span>
                      {' '}({Number(matchingSummary?.collarBandFrontTracePercent || 30).toFixed(0)}%)
                    </p>
                  </div>

                  <div className="rounded-md border border-dashed bg-muted/20 p-3 space-y-2" data-testid="shirt-collar-recompute-panel">
                    <p className="text-[11px] text-muted-foreground" data-testid="shirt-collar-recompute-note">
                      Solves the neck (bottom) handles and puts the top back on follow so it offsets the live bottom. Layout CbB0/CbB1 stay if you already nudged them; otherwise CbB0 sits 3/8&quot; above CbT0 and CbB1 uses Band Back Height.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => onRecomputeShirtCollarBand?.()}
                      disabled={!canEdit}
                      data-testid="shirt-collar-recompute-button"
                    >
                      <RotateCw size={14} className="mr-2" />
                      Recompute Collar Band
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground" data-testid="shirt-collar-band-note-text">
                    Click CbB0→CbF0 for two neck handles. The top follows the lower edge until you move a top handle. Pattern Pieces → Collar hides both the on-body overlay and the layout copy. Layout leaf: ClB0 defaults 1/4" behind CbB1; in Free Mode click ClB0 and nudge left/right to change that offset. Back Height stays the straight CbB1–ClB0 length. ClP0 from half Tie Gap; ClP1 from Point Length and Spread. Click ClB0→ClP1 for leaf handles. On-body leaf is that piece folded over the band top. Collar button sits at the midpoint of CbF0–CbF1; the hole runs across the stand.
                  </p>
                </AccordionContent>
              </AccordionItem>
              )}

              <AccordionItem value="shirt-buttons" className={activeShirtPanel === 'shirt-buttons' ? 'border rounded-lg bg-card px-3' : 'hidden'} data-testid="shirt-buttons-accordion-item">
                <AccordionTrigger className="hidden">
                  <div className="flex items-center gap-2">
                    <Settings size={16} className="text-primary" />
                    <span className="font-semibold text-sm">Button + Placket</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2 pb-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    {renderShirtInput('buttonCount', 'No. of Buttons (0=Auto)', measurements.buttonCount)}
                    {renderShirtInput('firstButtonPosition', 'First Button Below CF1', measurements.firstButtonPosition)}
                    {renderShirtInput('lastButtonPosition', 'Last Button Above CF7', measurements.lastButtonPosition)}
                    {renderShirtInput('buttonExtensionWidth', 'Button Extension Width', measurements.buttonExtensionWidth)}
                    {renderShirtInput('placketFoldWidth', 'Placket Fold Width', measurements.placketFoldWidth)}
                  </div>

                  <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-1" data-testid="shirt-button-summary-box">
                    <p data-testid="shirt-button-count-mode-text">Count mode: <span className="font-semibold uppercase">{matchingSummary?.buttonCountMode || 'auto'}</span></p>
                    <p data-testid="shirt-button-count-text">Resolved buttons: <span className="font-semibold">{matchingSummary?.buttonCount ?? 0}</span></p>
                    <p data-testid="shirt-button-spacing-text">Distance between buttons: <span className="font-semibold">{matchingSummary?.buttonSpacing ?? 0}"</span></p>
                  </div>

                  <p className="text-[11px] text-muted-foreground" data-testid="shirt-buttons-note-text">
                    Buttons are centered on the hidden CF line (CF1→CF7). E and PL are shown as visible parallel placket guides.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="shirt-formula" className={activeShirtPanel === 'shirt-formula' ? 'border rounded-lg bg-card px-3' : 'hidden'} data-testid="shirt-formula-accordion-item">
                <AccordionTrigger className="hidden">
                  <div className="flex items-center gap-2">
                    <Settings size={16} className="text-primary" />
                    <span className="font-semibold text-sm">Point Map + Correspondence</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2 pb-4 space-y-3">
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={onResetCurveHandles}
                      disabled={!canEdit}
                      data-testid="shirt-reset-curve-handles-button"
                    >
                      Reset Curve Handles
                    </Button>
                  </div>

                  <p className="text-[11px] text-muted-foreground" data-testid="shirt-curve-edit-instruction-text">
                    Curve edit flow: click a curve segment on canvas to reveal its handles, click a handle once to select, then drag to adjust.
                  </p>

                  {pointReferences.length > 0 ? (
                    <ul className="space-y-2 text-xs text-muted-foreground" data-testid="shirt-point-reference-list">
                      {pointReferences.map((item) => (
                        <li key={item} className="leading-relaxed">• {item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-muted-foreground" data-testid="shirt-point-reference-empty">Update any input to generate point references.</p>
                  )}

                  {matchingSummary?.neckHalfActual && (
                    <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-1" data-testid="shirt-matching-summary-box">
                      <p>Back shoulder: <span className="font-semibold">{matchingSummary.shoulderBackLength}"</span></p>
                      <p>Front shoulder: <span className="font-semibold">{matchingSummary.shoulderFrontLength}"</span></p>
                      <p>Half neck actual: <span className="font-semibold">{matchingSummary.neckHalfActual}"</span></p>
                      <p>Half neck target: <span className="font-semibold">{matchingSummary.neckHalfTargetMin}" to {matchingSummary.neckHalfTargetMax}"</span></p>
                      <p data-testid="shirt-total-neck-full-text">Total neck full: <span className="font-semibold">{matchingSummary.totalNeckFull}"</span></p>
                      <p data-testid="shirt-total-armhole-full-text">Total armhole full: <span className="font-semibold">{matchingSummary.totalArmholeFull}"</span></p>
                      <p data-testid="shirt-front-waist-line-text">
                        Front waist raw (CF4→FWP): <span className="font-semibold">{matchingSummary.frontWaistLine}"</span>
                        {Number(matchingSummary?.frontWaistDartIntake || 0) > 0 && (
                          <> · net after fish dart: <span className="font-semibold">{matchingSummary.frontWaistNetLine}"</span></>
                        )}
                      </p>
                      <p data-testid="shirt-back-waist-raw-line-text">Back waist raw (CB4→BWP): <span className="font-semibold">{matchingSummary.backWaistRawLine}"</span></p>
                      <p data-testid="shirt-waist-dart-intake-text">Waist dart (BD1→BD2): <span className="font-semibold">{matchingSummary.waistDartIntake}"</span></p>
                      <p data-testid="shirt-back-waist-net-line-text">Back waist net: <span className="font-semibold">{matchingSummary.backWaistNetLine}"</span></p>
                      <p data-testid="shirt-total-waist-width-text">Waist Target Full: <span className="font-semibold">{matchingSummary.targetWaistFull}"</span></p>
                      <p data-testid="shirt-total-waist-raw-full-text">Waist Raw Drafted Full: <span className="font-semibold">{matchingSummary.totalWaistRawFull ?? matchingSummary.totalWaistFull}"</span></p>
                      <p data-testid="shirt-total-waist-net-full-text">Waist Net After Dart Full: <span className="font-semibold">{matchingSummary.totalWaistFull}"</span></p>
                      <p data-testid="shirt-total-belly-width-text">Belly Target Full: <span className="font-semibold">{matchingSummary.targetBellyFull}"</span></p>
                      <p data-testid="shirt-total-belly-raw-full-text">Belly Raw Drafted Full: <span className="font-semibold">{matchingSummary.totalBellyRawFull ?? matchingSummary.totalBellyFull}"</span></p>
                      <p data-testid="shirt-total-belly-net-full-text">Belly Net After Dart Full: <span className="font-semibold">{matchingSummary.totalBellyFull}"</span></p>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            </div>
          </div>
        </ScrollArea>

        <div className="p-3 border-t bg-muted/10 text-xs text-muted-foreground" data-testid="shirt-left-panel-footer">
          <div className="flex items-center justify-between">
            <span>
              Block: <span className="text-emerald-600 font-semibold">
                {patternData?.sleevePath ? 'BASE BLOCK · FRONT/BACK/YOKE/SLEEVE' : 'BASE BLOCK · FRONT/BACK/YOKE'}
              </span>
            </span>
            <span>Status: {garmentStatus.toUpperCase()}</span>
          </div>
        </div>

        {renderAdvancedPanel()}
      </aside>
    );
  }

  const jacketBellyProfileLabelMap = {
    slim: 'Slim',
    normal: 'Normal',
    slight_belly: 'Slight Belly',
    big_belly: 'Big Belly',
    extreme_belly: 'Extreme Belly',
  };

  const jacketPostureLabelMap = {
    normal: 'Normal',
    stoop: 'Stoop',
    sway: 'Sway',
  };

  if (garmentType === 'jacket') {
    const pointReferences = Array.isArray(patternData?.pointReferences) ? patternData.pointReferences : [];
    const matchingSummary = patternData?.matchingSummary || {};
    const lapelSummary = patternData?.lapelSummary || {};
    const isChestPocketEnabled = measurements?.chestPocketEnabled !== false && measurements?.chestPocketEnabled !== 0 && measurements?.chestPocketEnabled !== '0';
    const isSidePocketEnabled = measurements?.sidePocketEnabled !== false && measurements?.sidePocketEnabled !== 0 && measurements?.sidePocketEnabled !== '0';
    const jacketButtonLayout = ['one_button', 'two_buttons', 'three_roll_two'].includes(String(measurements?.jacketButtonLayout || '').toLowerCase())
      ? String(measurements?.jacketButtonLayout || '').toLowerCase()
      : 'two_buttons';
    const hasLowerJacketButton = jacketButtonLayout === 'two_buttons' || jacketButtonLayout === 'three_roll_two';
    const hasUpperJacketButton = jacketButtonLayout === 'three_roll_two';
    const activeJacketFrontClosure = ['single_breasted', 'double_breasted'].includes(String(measurements?.jacketFrontClosure || '').toLowerCase())
      ? String(measurements?.jacketFrontClosure || '').toLowerCase()
      : 'single_breasted';
    const isDoubleBreastedClosure = activeJacketFrontClosure === 'double_breasted';

    const jacketTabs = [
      { key: 'body', label: 'Body Inputs', icon: Shirt, testId: 'jacket-panel-tab-body' },
      { key: 'lapel', label: 'Lapel', icon: Gem, testId: 'jacket-panel-tab-lapel' },
      { key: 'sleeve', label: 'Jacket Sleeve', icon: RulerDimensionLine, testId: 'jacket-panel-tab-sleeve' },
      { key: 'panel', label: 'Front Darts', icon: Fish, testId: 'jacket-panel-tab-fish-dart' },
      { key: 'pocket', label: 'Pockets', icon: WalletCards, testId: 'jacket-panel-tab-pocket' },
      { key: 'button', label: 'Buttons', icon: CircleDot, testId: 'jacket-panel-tab-button' },
      { key: 'formula', label: 'Point Map + Curve Controls', icon: SigmaSquare, testId: 'jacket-panel-tab-formula' },
    ];
    const jacketDraftLabel = String(patternData?.geometryVersion || '').toLowerCase() === 'jacket_geom_v1'
      ? 'J-SIY-V1'
      : 'J-SIY-V0';

    const activeJacketLapelType = ['pointed_notch', 'rounded_notch', 'peak', 'shawl', 'diamond_shawl'].includes(String(measurements?.lapelType || '').toLowerCase())
      ? String(measurements?.lapelType || '').toLowerCase()
      : 'pointed_notch';
    const lapelTypeOptions = [
      { value: 'pointed_notch', label: 'Pointed Notch', enabled: true },
      { value: 'rounded_notch', label: 'Rounded Notch', enabled: true },
      { value: 'peak', label: 'Peak', enabled: true },
      { value: 'shawl', label: 'Shawl', enabled: true },
      { value: 'diamond_shawl', label: 'Diamond Shawl', enabled: true },
    ];
    const jacketFrontClosureLabelMap = {
      single_breasted: 'Single Breasted',
      double_breasted: 'Double Breasted',
    };

    const lapelSummaryTitleMap = {
      pointed_notch: 'Current Pointed Notch Summary',
      rounded_notch: 'Current Rounded Notch Summary',
      peak: 'Current Peak Lapel Summary',
      shawl: 'Current Shawl Collar Summary',
      diamond_shawl: 'Current Diamond Shawl Summary',
    };

    const lapelSummaryDetailMap = {
      pointed_notch: [
        { key: 'backNeckCurveLength', label: 'Back Neck Curve', value: lapelSummary?.backNeckCurveLength || 0 },
        { key: 'collarMatchSegmentLength', label: 'UCOL0-UCOL1 Match', value: lapelSummary?.collarMatchSegmentLength || 0 },
        { key: 'openingWidth', label: 'Opening Width', value: lapelSummary?.openingWidth || 0 },
        { key: 'rollFallLength', label: 'LP0-RL1 Fall', value: lapelSummary?.rollFallLength || 0 },
      ],
      rounded_notch: [
        { key: 'backNeckCurveLength', label: 'Back Neck Curve', value: lapelSummary?.backNeckCurveLength || 0 },
        { key: 'collarMatchSegmentLength', label: 'UCOL0-UCOL1 Match', value: lapelSummary?.collarMatchSegmentLength || 0 },
        { key: 'roundedCurveLength', label: 'RN1-RN2 Curve', value: lapelSummary?.roundedCurveLength || 0 },
        { key: 'rollFallLength', label: 'RN2-RL1 Fall', value: lapelSummary?.rollFallLength || 0 },
        { key: 'collarRoundLength', label: 'CP0 Collar Curve', value: lapelSummary?.collarRoundLength || 0 },
      ],
      peak: [
        { key: 'backNeckCurveLength', label: 'Back Neck Curve', value: lapelSummary?.backNeckCurveLength || 0 },
        { key: 'collarMatchSegmentLength', label: 'UCOL0-UCOL1 Match', value: lapelSummary?.collarMatchSegmentLength || 0 },
        { key: 'peakRise', label: 'Peak Rise', value: lapelSummary?.peakRise || 0 },
        { key: 'peakTipDistance', label: 'LP2-LP3', value: lapelSummary?.peakTipDistance || 0 },
      ],
      shawl: [
        { key: 'backNeckCurveLength', label: 'Back Neck Curve', value: lapelSummary?.backNeckCurveLength || 0 },
        { key: 'collarMatchSegmentLength', label: 'FHPS-UCOL1 Match', value: lapelSummary?.collarMatchSegmentLength || 0 },
        { key: 'shawlUpperStraightLength', label: 'FHPS-TCOL2 Chain', value: lapelSummary?.shawlUpperStraightLength || 0 },
        { key: 'shawlOuterCurveLength', label: 'TCOL2-RL1 Cubic', value: lapelSummary?.shawlOuterCurveLength || 0 },
      ],
      diamond_shawl: [
        { key: 'backNeckCurveLength', label: 'Back Neck Curve', value: lapelSummary?.backNeckCurveLength || 0 },
        { key: 'collarMatchSegmentLength', label: 'FHPS-UCOL1 Match', value: lapelSummary?.collarMatchSegmentLength || 0 },
        { key: 'shawlUpperStraightLength', label: 'FHPS-TCOL2 Chain', value: lapelSummary?.shawlUpperStraightLength || 0 },
        { key: 'diamondQuadraticLength', label: 'TCOL2-RL2 Quadratic', value: lapelSummary?.diamondQuadraticLength || 0 },
        { key: 'diamondSegmentLength', label: 'RL2-RL1 Straight', value: lapelSummary?.diamondSegmentLength || 0 },
      ],
    };
    const isShawlFamilyLapelSummary = ['shawl', 'diamond_shawl'].includes(lapelSummary?.lapelType);

    const jacketButtonLayoutLabelMap = {
      one_button: '1 Button',
      two_buttons: '2 Buttons',
      three_roll_two: '3 Roll 2',
    };
    const jacketDartModeLabelMap = {
      off: 'Off',
      fish: 'Fish Dart',
      belly_and_fish: 'Belly + Fish Dart',
    };
    const currentJacketDartMode = ['off', 'fish', 'belly_and_fish'].includes(String(measurements?.jacketDartMode || '').toLowerCase())
      ? String(measurements?.jacketDartMode || '').toLowerCase()
      : ((measurements?.fishDartEnabled !== false && measurements?.fishDartEnabled !== 0 && measurements?.fishDartEnabled !== '0') ? 'belly_and_fish' : 'off');
    const hasFrontFishDart = currentJacketDartMode !== 'off';
    const hasFrontBellyDart = currentJacketDartMode === 'belly_and_fish';
    const renderJacketFrontSideSeamMatchInfo = () => (
      <div className="space-y-1 text-[11px] text-muted-foreground" data-testid="jacket-front-side-seam-segment-lengths-small-text">
        <p>Front panel vs side panel seam segments (Front / Side / Δ):</p>
        <p data-testid="jacket-front-side-seam-chest-waist-text">
          C→W: {Number(matchingSummary?.frontPanelChestToWaist || 0).toFixed(3)}&quot; /
          {' '}{Number(matchingSummary?.sidePanelChestToWaist || 0).toFixed(3)}&quot; /
          {' '}{Number(matchingSummary?.frontToSideChestWaistDelta || 0).toFixed(3)}&quot;
        </p>
        <p data-testid="jacket-front-side-seam-waist-belly-text">
          W→B: {Number(matchingSummary?.frontPanelWaistToBelly || 0).toFixed(3)}&quot; /
          {' '}{Number(matchingSummary?.sidePanelWaistToBelly || 0).toFixed(3)}&quot; /
          {' '}{Number(matchingSummary?.frontToSideWaistBellyDelta || 0).toFixed(3)}&quot;
        </p>
        <p data-testid="jacket-front-side-seam-belly-seat-text">
          B→H: {Number(matchingSummary?.frontPanelBellyToSeat || 0).toFixed(3)}&quot; /
          {' '}{Number(matchingSummary?.sidePanelBellyToSeat || 0).toFixed(3)}&quot; /
          {' '}{Number(matchingSummary?.frontToSideBellyToSeatDelta || 0).toFixed(3)}&quot;
        </p>
        <p data-testid="jacket-front-side-seam-total-text">
          Total: {Number(matchingSummary?.frontPanelSideSeamTotal || 0).toFixed(3)}&quot; /
          {' '}{Number(matchingSummary?.sidePanelFrontSeamTotal || 0).toFixed(3)}&quot; /
          {' '}{Number(matchingSummary?.frontToSideTotalDelta || 0).toFixed(3)}&quot;
        </p>
        <p data-testid="jacket-front-side-seam-gap-text">
          Belly cut gap BD1→BD2: {Number(matchingSummary?.frontPanelBellyCutGap || 0).toFixed(3)}&quot;
        </p>
      </div>
    );

    const currentJacketBellyProfile = String(measurements?.bellyProfile || 'normal').toLowerCase();
    const currentJacketBellyRule = (() => {
      if (currentJacketBellyProfile === 'slim') return { frontSideOffset: 1.25, cfForwardOffset: 0, swpfOffsetFromScpf: -0.5 };
      if (currentJacketBellyProfile === 'slight_belly') return { frontSideOffset: 0.75, cfForwardOffset: 0.25, swpfOffsetFromScpf: 0 };
      if (currentJacketBellyProfile === 'big_belly') return { frontSideOffset: 0.5, cfForwardOffset: 0.5, swpfOffsetFromScpf: 0.25 };
      if (currentJacketBellyProfile === 'extreme_belly') return { frontSideOffset: 0.375, cfForwardOffset: 1, swpfOffsetFromScpf: 0.5 };
      return { frontSideOffset: 1, cfForwardOffset: 0, swpfOffsetFromScpf: -0.25 };
    })();

    const jacketFormulaSections = {
      front: [
        'CF0 vertical offset follows Bk to FT HPS exactly: CF0.y = CB0.y + backToFtHps.',
        'FHPS shares CF0 y-level. RL0 shares FHPS y-level and is placed +1" in x from FHPS.',
        'FND lies on Roll Line (RL0→RL1), and FHPS→FND is now a soft editable neck curve.',
        'Collar draft traces 1.375" from FND along the front neck curve, crosses the shoulder seam at UCOL0 placed 0.5" from FHPS toward FLPS, then continues in one straight line to UCOL1 by the exact back-neck curve length.',
        'UCOL2→FND and TCOL2→CP0_M are editable collar curves with dedicated control handles.',
        'X-Front depth override controls CF0→CF1 (and FXF y-level) vertical drop.',
        'Front armhole is split FLPS→FXF and FXF→FCPS with independent segment controls.',
        `Jacket posture: ${String(measurements?.posture || 'normal').replaceAll('_', ' ')} | Bk to FT HPS seeds backToFtHps = ${Number(measurements?.backToFtHps || 0).toFixed(3)}" unless manually edited later.`,
        `Jacket belly profile: ${(String(measurements?.bellyProfile || 'normal')).replaceAll('_', ' ')} | FWPS/FBPS/FSPS = FCPS + ${Number(currentJacketBellyRule.frontSideOffset).toFixed(3)}" | CF3/CF4 = CF0 + ${Number(currentJacketBellyRule.cfForwardOffset).toFixed(3)}" in x before manual offsets.`,
        'Jacket Length Front is resolved as vertical distance from FHPS to SP1.',
        'Front fish dart points: FD1 is 65% from CF3→FWPS (with optional waist shift), FD2 = FD1 + dart width, FD0/FD3 are top/bottom lengths.',
      ],
      side: [
        'Side panel balance follows: Side = TotalHalf - Front - Back at chest/waist/belly/seat.',
        'SCPF is tied to FCPS using the side chest delta, while SCPB is anchored from SAHP.',
        `SWPF follows Belly Profile from SCPF: ${Number(currentJacketBellyRule.swpfOffsetFromScpf).toFixed(3)}" before manual offsets.`,
        'SBPF is seeded forward from SWPF, and SSPF continues from SBPF before the back-side balance is solved.',
        'Side front and side back seams use independent curve controls for arm, chest→waist, waist→belly, and belly→seat shaping.',
      ],
      back: [
        'Origin: CB0 = (0,0). +X toward center-front, +Y toward hem.',
        'Across-Back sets the shoulder/chest split reference used by BXB.',
        'X-Back depth override controls CB0→CB3/BXB vertical drop.',
        'Back armhole is split BLPS→BXB and BXB→BAHP with independent segment controls.',
        'Point XY offsets are in 1/16" steps for key points (CB4, BLPS, BXB, FLPS, FXF, BWPS, FWPS, BBPS, FBPS, BSPS, FSPS).',
        'Free Mode (when active) applies point deltas separately from measurements and does not overwrite base parametric inputs.',
      ],
      sleeve: [
        `BiB is vertically aligned to CF2 for Jacket Sleeve V1 (${Number(matchingSummary?.sleeveCapHeight || 0).toFixed(3)}" cap height at current solve).`,
        `BP is seeded about 5" to the right of LP0_M, while HB follows ElB with +2.25" in x before Free Mode edits.`,
        `Front Pitch Height % of Cap = ${(Number(measurements?.sleeveFrontPitchPercent || 0.7) * 100).toFixed(1)}% | Back Pitch Height % of Cap = ${(Number(measurements?.sleeveBackPitchPercent || 0.35) * 100).toFixed(1)}%.`,
        `Distance to Elbow from SLT = ${Number(matchingSummary?.sleeveDistToElbow || 0).toFixed(3)}" | Auto seed = ${Number(matchingSummary?.sleeveDistToElbowAuto || 0).toFixed(3)}" when field is 0.`,
        `Sleeve cap target = ${Number(matchingSummary?.sleeveCapTarget || 0).toFixed(3)}" | solved cap path = ${Number(matchingSummary?.sleeveCapPathLength || 0).toFixed(3)}".`,
        'BiF / USBiF / TSBiF are helper points only in V1. The final front seams run directly USFP→USElF→USHF and FP→TSFP→TSElF→TSHF.',
        'USFP is solved on the BiC→FP curve, and the dashed guide from USFP to FP shows the remaining front cap arc.',
      ],
      bellyProfile: [
        `Current Belly Profile: ${currentJacketBellyProfile.replaceAll('_', ' ')}`,
        `FWPS / FBPS / FSPS = FCPS + ${Number(currentJacketBellyRule.frontSideOffset).toFixed(3)}"`,
        `CF3 / CF4 = CF0 + ${Number(currentJacketBellyRule.cfForwardOffset).toFixed(3)}" in x`,
        `SWPF = SCPF + ${Number(currentJacketBellyRule.swpfOffsetFromScpf).toFixed(3)}"`,
        'Profile map: Slim = +1.25 / +0 / -0.5 | Normal = +1.0 / +0 / -0.25 | Slight Belly = +0.75 / +0.25 / 0 | Big Belly = +0.5 / +0.5 / +0.25 | Extreme Belly = +0.375 / +1.0 / +0.5 (Front side seam / CF3-4 / SWPF).',
      ],
    };

    const resolveJacketDrawnValueText = (key) => {
      const numericMap = {
        chest: Number((matchingSummary?.chestHalfDrawn || 0) * 2).toFixed(3),
        waist: Number((matchingSummary?.waistHalfDrawn || 0) * 2).toFixed(3),
        belly: Number((matchingSummary?.bellyHalfDrawn || 0) * 2).toFixed(3),
        seat: Number((matchingSummary?.seatHalfDrawn || 0) * 2).toFixed(3),
        acrossFront: Number(matchingSummary?.acrossFrontDrawn || 0).toFixed(3),
        acrossBack: Number(matchingSummary?.acrossBackDrawn || 0).toFixed(3),
        jacketLengthBack: Number(matchingSummary?.backLengthDrawn || 0).toFixed(3),
        jacketLengthFront: Number(matchingSummary?.frontLengthDrawn || 0).toFixed(3),
      };

      if (Object.prototype.hasOwnProperty.call(numericMap, key)) {
        const drawnValue = numericMap[key];
        const deviation = formatDeviation(measurements?.[key], drawnValue);
        return `Drawn: ${drawnValue}\"${deviation ? ` | Δ ${deviation}\"` : ''}`;
      }

      if (key === 'shoulder') {
        return `Drawn Shoulders — Front: ${Number(matchingSummary?.frontShoulderDrawn || 0).toFixed(3)}\" | Back: ${Number(matchingSummary?.backShoulderDrawn || 0).toFixed(3)}\"`;
      }

      if (key === 'armholeScyeDepth') {
        const averageVertical = Number(matchingSummary?.averageScyeVertical || 0).toFixed(3);
        const deviation = formatDeviation(measurements?.armholeScyeDepth, averageVertical);
        return `Drawn avg vertical: ${averageVertical}\"${deviation ? ` | Δ ${deviation}\"` : ''} | TAH: ${Number(matchingSummary?.totalArmholeCurve || 0).toFixed(3)}\"`;
      }

      if (key === 'backToFtHps') {
        return `Drawn: ${Number((matchingSummary?.resolvedFrontNeckWidth || 0) - (matchingSummary?.resolvedBackNeckWidth || 0)).toFixed(3)}\"`;
      }

      if (key === 'jacketSleeveLength') {
        const drawnLength = Number(matchingSummary?.jacketSleeveLengthMidpointDrawn || 0).toFixed(3);
        const deviation = formatDeviation(measurements?.jacketSleeveLength, drawnLength);
        return `Drawn SLT→mid(HB–TSHF): ${drawnLength}\"${deviation ? ` | Δ ${deviation}\"` : ''}`;
      }

      return null;
    };

    const resolveJacketSleeveSplitText = (group) => {
      const map = {
        bicep: {
          top: Number(matchingSummary?.jacketSleeveTopBicepDrawn || 0).toFixed(3),
          under: Number(matchingSummary?.jacketSleeveUnderBicepDrawn || 0).toFixed(3),
          total: Number(matchingSummary?.jacketSleeveBicepTotalDrawn || 0).toFixed(3),
          input: Number(measurements?.jacketSleeveBicep || 0).toFixed(3),
        },
        elbow: {
          top: Number(matchingSummary?.jacketSleeveTopElbowDrawn || 0).toFixed(3),
          under: Number(matchingSummary?.jacketSleeveUnderElbowDrawn || 0).toFixed(3),
          total: Number(matchingSummary?.jacketSleeveElbowTotalDrawn || 0).toFixed(3),
          input: Number(measurements?.jacketSleeveElbow || 0).toFixed(3),
        },
        hem: {
          top: Number(matchingSummary?.jacketSleeveTopHemDrawn || 0).toFixed(3),
          under: Number(matchingSummary?.jacketSleeveUnderHemDrawn || 0).toFixed(3),
          total: Number(matchingSummary?.jacketSleeveHemTotalDrawn || 0).toFixed(3),
          input: Number(measurements?.jacketSleeveHem || 0).toFixed(3),
        },
      };

      const values = map[group];
      if (!values) return null;
      return `TS ${values.top}\" • US ${values.under}\" • Total ${values.total}\" / Input ${values.input}\"`;
    };

    const resolveJacketFrontSeamText = (piece) => {
      if (piece === 'ts') {
        return `TS front seam — FP→TSFP ${Number(matchingSummary?.jacketSleeveTsFrontSeam_fpToTsfp || 0).toFixed(3)}\", TSFP→TSElF ${Number(matchingSummary?.jacketSleeveTsFrontSeam_tsfpToTsElf || 0).toFixed(3)}\", TSElF→TSHF ${Number(matchingSummary?.jacketSleeveTsFrontSeam_tsElfToTsHf || 0).toFixed(3)}\" • Total ${Number(matchingSummary?.jacketSleeveTsFrontSeamTotal || 0).toFixed(3)}\"`;
      }

      return `US front seam — USFP→USElF ${Number(matchingSummary?.jacketSleeveUsFrontSeam_usfpToUsElf || 0).toFixed(3)}\", USElF→USHF ${Number(matchingSummary?.jacketSleeveUsFrontSeam_usElfToUsHf || 0).toFixed(3)}\" • Total ${Number(matchingSummary?.jacketSleeveUsFrontSeamTotal || 0).toFixed(3)}\"`;
    };

    const resolveJacketDrawnNumber = (...candidates) => {
      const match = candidates.find((value) => Number.isFinite(Number(value)));
      return match === undefined ? 0 : Number(match);
    };

    const jacketDrawnRows = [
      { key: 'neck', label: 'Neck', drawnValue: resolveJacketDrawnNumber(measurements?.neck, referenceValueMap.neck), seedValue: referenceValueMap.neck, advancedKey: 'neck', onOpenAdvanced: setActiveJacketAdvancedControl },
      { key: 'chest', label: 'Chest', drawnValue: (matchingSummary?.chestHalfDrawn || 0) * 2, seedValue: referenceValueMap.chest },
      { key: 'waist', label: 'Waist', drawnValue: (matchingSummary?.waistHalfDrawn || 0) * 2, seedValue: referenceValueMap.waist, advancedKey: 'waist', onOpenAdvanced: setActiveJacketAdvancedControl },
      { key: 'belly', label: 'Belly', drawnValue: (matchingSummary?.bellyHalfDrawn || 0) * 2, seedValue: referenceValueMap.belly, advancedKey: 'belly', onOpenAdvanced: setActiveJacketAdvancedControl },
      { key: 'seat', label: 'Seat/Hem', drawnValue: (matchingSummary?.seatHalfDrawn || 0) * 2, seedValue: referenceValueMap.seat },
      { key: 'shoulder', label: 'Shoulder', drawnValue: resolveJacketDrawnNumber(measurements?.shoulder, referenceValueMap.shoulder), seedValue: referenceValueMap.shoulder, advancedKey: 'shoulder', onOpenAdvanced: setActiveJacketAdvancedControl },
      { key: 'acrossBack', label: 'X-Back', drawnValue: matchingSummary?.acrossBackDrawn, seedValue: referenceValueMap.acrossBack, advancedKey: 'xBack', onOpenAdvanced: setActiveJacketAdvancedControl },
      { key: 'acrossFront', label: 'X-Front', drawnValue: matchingSummary?.acrossFrontDrawn, seedValue: referenceValueMap.acrossFront, advancedKey: 'xFront', onOpenAdvanced: setActiveJacketAdvancedControl },
      {
        key: 'armholeScyeDepth',
        label: 'Scye Depth',
        drawnValue: matchingSummary?.averageScyeVertical,
        seedValue: referenceValueMap.armholeScyeDepth,
        helperText: `TAH ${Number(matchingSummary?.totalArmholeCurve || 0).toFixed(3)}"`,
      },
      { key: 'jacketLengthBack', label: 'Back Length', drawnValue: matchingSummary?.backLengthDrawn, seedValue: referenceValueMap.jacketLengthBack },
      { key: 'jacketLengthFront', label: 'Front Length', drawnValue: matchingSummary?.frontLengthDrawn, seedValue: referenceValueMap.jacketLengthFront },
      {
        key: 'jacketSleeveBicep',
        label: 'Biceps',
        drawnValue: resolveJacketDrawnNumber(matchingSummary?.jacketSleeveBicepTotalDrawn, measurements?.jacketSleeveBicep),
        seedValue: referenceValueMap.jacketSleeveBicep,
      },
      {
        key: 'jacketSleeveElbow',
        label: 'Elbow',
        drawnValue: resolveJacketDrawnNumber(matchingSummary?.jacketSleeveElbowTotalDrawn, measurements?.jacketSleeveElbow),
        seedValue: referenceValueMap.jacketSleeveElbow,
        advancedKey: 'elbow',
        onOpenAdvanced: setActiveJacketAdvancedControl,
      },
      {
        key: 'jacketSleeveHem',
        label: 'Wrist',
        drawnValue: resolveJacketDrawnNumber(matchingSummary?.jacketSleeveHemTotalDrawn, measurements?.jacketSleeveHem),
        seedValue: referenceValueMap.jacketSleeveHem,
      },
      {
        key: 'jacketSleeveLength',
        label: 'Sleeve Length',
        drawnValue: resolveJacketDrawnNumber(matchingSummary?.jacketSleeveLengthMidpointDrawn, measurements?.jacketSleeveLength),
        seedValue: referenceValueMap.jacketSleeveLength,
      },
      {
        key: 'lapelWidth',
        label: 'Lapel Width',
        drawnValue: resolveJacketDrawnNumber(patternData?.lapelSummary?.lapelWidth, measurements?.lapelWidth),
        seedValue: referenceValueMap.lapelWidth,
      },
    ];

    const renderJacketInput = (key, label, options = {}) => {
      const inputExternalValue = options.inputValue ?? measurements?.[key] ?? '';

      return (
      <div key={key} className={DENSE_FIELD_STACK_CLASS}>
        <div className={DENSE_FIELD_ROW_CLASS}>
          <Label className={DENSE_LABEL_CLASS}>{label}</Label>
          {options.advancedKey && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-4 w-4 p-0 text-slate-500"
              onClick={() => setActiveJacketAdvancedControl(options.advancedKey)}
              disabled={!canEdit}
              data-testid={`jacket-${key}-advanced-trigger-button`}
            >
              <SlidersHorizontal size={10} />
            </Button>
          )}
        </div>
        <Input
          type="number"
          step={options.step || 0.125}
          {...getBufferedInputHandlers(key, inputExternalValue, {
            readOnly: options.readOnly,
          })}
          disabled={!canEdit || options.readOnly}
          data-testid={`jacket-${key}-input`}
          className={`${DENSE_INPUT_CLASS} ${isModified(key) ? 'border-orange-400 bg-orange-50/30' : 'bg-white'} ${(!canEdit || options.readOnly) ? 'bg-muted cursor-not-allowed' : ''}`}
        />
        {(options.drawnText || resolveJacketDrawnValueText(key)) && (
          <p className="text-[10px] leading-4 text-emerald-700 font-medium" data-testid={`jacket-${key}-drawn-value-text`}>
            {options.drawnText || resolveJacketDrawnValueText(key)}
          </p>
        )}
        {key === 'armholeScyeDepth' && (
          <p className="text-[10px] leading-4 text-emerald-700 font-medium" data-testid="jacket-armholeScyeDepth-vertical-drawn-value-text">
            Avg Scye Vertical: {Number(matchingSummary?.averageScyeVertical || 0).toFixed(3)}&quot;
          </p>
        )}
      </div>
    );
    };

    const renderJacketSelect = (key, label, options = {}) => {
      const optionMap = options.optionMap || (key === 'posture' ? jacketPostureLabelMap : jacketBellyProfileLabelMap);
      const fallbackValue = options.defaultValue || 'normal';
      const resolvedValue = String(measurements?.[key] || fallbackValue);

      return (
      <div key={key} className={DENSE_FIELD_STACK_CLASS}>
        <div className={DENSE_FIELD_ROW_CLASS}>
          <Label className="text-[8px] font-medium uppercase tracking-[0.16em] text-slate-500">{label}</Label>
          {options.advancedKey && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-4 w-4 p-0 text-slate-500"
              onClick={() => setActiveJacketAdvancedControl(options.advancedKey)}
              disabled={!canEdit}
              data-testid={`jacket-${key}-advanced-trigger-button`}
            >
              <SlidersHorizontal size={10} />
            </Button>
          )}
        </div>
        <Select value={resolvedValue} onValueChange={(value) => onMeasurementChange(key, value)} disabled={!canEdit}>
          <SelectTrigger data-testid={`jacket-${key}-select-trigger`} className={`${DENSE_SELECT_TRIGGER_CLASS} ${isModified(key) ? 'border-orange-400 bg-orange-50/30' : 'bg-white'}`}>
            <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(optionMap).map(([value, text]) => (
              <SelectItem key={value} value={value} data-testid={`jacket-${key}-option-${value}`}>
                {text}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      );
    };

    const renderJacketOverrideControl = ({ section, label, enabledKey, valueKey, autoValue, drawnValue }) => {
      const enabled = Boolean(measurements?.[enabledKey]);
      const hasManualValue = measurements?.[valueKey] !== null && measurements?.[valueKey] !== undefined && measurements?.[valueKey] !== '';
      const currentValue = hasManualValue ? measurements?.[valueKey] : '';

      const handleOverrideToggle = (checked) => {
        if (!checked) {
          onMeasurementChange(enabledKey, false);
          return;
        }

        const drawnNumeric = Number(drawnValue);
        const autoNumeric = Number(autoValue);
        const seedValue = Number.isFinite(drawnNumeric)
          ? drawnNumeric
          : (Number.isFinite(autoNumeric) ? autoNumeric : 0);

        if (!initializedOverrideKeys[valueKey]) {
          onMeasurementChange(valueKey, Number(seedValue.toFixed(3)));
          setInitializedOverrideKeys((prev) => ({ ...prev, [valueKey]: true }));
        }

        onMeasurementChange(enabledKey, true);
      };

      return (
        <div className="rounded-xl border border-slate-200 bg-white/90 p-2.5 space-y-2 shadow-sm" data-testid={`jacket-${section}-${valueKey}-override-row`}>
          <div className="flex items-center justify-between gap-3">
            <Label className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</Label>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-muted-foreground">Override</span>
              <Switch
                checked={enabled}
                onCheckedChange={handleOverrideToggle}
                disabled={!canEdit}
                data-testid={`jacket-${section}-${valueKey}-override-switch`}
              />
            </div>
          </div>

          <p className="text-[10px] text-muted-foreground" data-testid={`jacket-${section}-${valueKey}-auto-text`}>
            Auto: {Number(autoValue ?? 0).toFixed(3)}&quot;
          </p>

          {enabled && (
            <Input
              type="number"
              step="0.125"
              {...getBufferedInputHandlers(valueKey, currentValue)}
              disabled={!canEdit}
              data-testid={`jacket-${section}-${valueKey}-override-input`}
              className={`h-8 rounded-lg border-slate-200 text-sm ${isModified(valueKey) ? 'border-orange-400 bg-orange-50/30' : 'bg-white'} ${!canEdit ? 'bg-muted cursor-not-allowed' : ''}`}
            />
          )}

          {drawnValue !== undefined && drawnValue !== null && (
            <p className="text-[10px] leading-4 text-emerald-700 font-medium" data-testid={`jacket-${section}-${valueKey}-drawn-text`}>
              Drawn: {Number(drawnValue).toFixed(3)}&quot;
            </p>
          )}
        </div>
      );
    };

    const renderJacketAdvancedPanel = () => {
      if (!activeJacketAdvancedControl) return null;

      const panelMap = {
        neck: {
          title: 'Neck Advanced Controls',
          content: (
            <div className="space-y-3" data-testid="jacket-neck-override-control-list">
              {renderJacketOverrideControl({
                section: 'neck',
                label: 'Front Neck Width (FNW)',
                enabledKey: 'overrideFNWEnabled',
                valueKey: 'overrideFNW',
                autoValue: matchingSummary.autoFrontNeckWidth,
                drawnValue: matchingSummary.resolvedFrontNeckWidth,
              })}
              {renderJacketOverrideControl({
                section: 'neck',
                label: 'Back Neck Width (BNW)',
                enabledKey: 'overrideBNWEnabled',
                valueKey: 'overrideBNW',
                autoValue: matchingSummary.autoBackNeckWidth,
                drawnValue: matchingSummary.resolvedBackNeckWidth,
              })}
              {renderJacketOverrideControl({
                section: 'neck',
                label: 'Front Neck Depth (FND)',
                enabledKey: 'overrideFNDEnabled',
                valueKey: 'overrideFND',
                autoValue: matchingSummary.autoFrontNeckDepth,
                drawnValue: matchingSummary.resolvedFrontNeckDepth,
              })}
              {renderJacketOverrideControl({
                section: 'neck',
                label: 'Back Neck Depth (BND)',
                enabledKey: 'overrideBNDEnabled',
                valueKey: 'overrideBND',
                autoValue: matchingSummary.autoBackNeckDepth,
                drawnValue: matchingSummary.resolvedBackNeckDepth,
              })}
              <div className="rounded-md border border-dashed bg-muted/20 p-3 space-y-2" data-testid="jacket-neck-recompute-collar-panel">
                <p className="text-[11px] text-muted-foreground" data-testid="jacket-neck-recompute-collar-note">
                  Recompute collar points from the current neck, shoulder, and lapel geometry after Free Edit changes.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => onRecomputeJacketCollar?.()}
                  disabled={!canEdit}
                  data-testid="jacket-neck-recompute-collar-button"
                >
                  <RotateCw size={14} className="mr-2" />
                  Recompute Collar
                </Button>
              </div>
            </div>
          ),
        },
        shoulder: {
          title: 'Shoulder Advanced Controls',
          content: (
            <div className="space-y-3">
              {renderJacketInput('backShoulderSlope', 'Back Shoulder Slope')}
              {renderJacketInput('frontShoulderSlope', 'Front Shoulder Slope')}
              {renderJacketInput('backToFtHps', 'Bk to FT HPS')}
              <div className="grid grid-cols-2 gap-3" data-testid="jacket-shoulder-curve-control-grid">
                {renderJacketInput('backShoulderCurveCtrlDx', 'Back Shoulder Curve X', { step: 0.125 })}
                {renderJacketInput('backShoulderCurveCtrlDy', 'Back Shoulder Curve Y', { step: 0.125 })}
                {renderJacketInput('frontShoulderCurveCtrlDx', 'Front Shoulder Curve X', { step: 0.125 })}
                {renderJacketInput('frontShoulderCurveCtrlDy', 'Front Shoulder Curve Y', { step: 0.125 })}
              </div>
              <p className="text-[11px] text-muted-foreground" data-testid="jacket-shoulder-advanced-default-note">
                Defaults: Back Shoulder Slope = 1&quot;, Front Shoulder Slope = 1.25&quot;, Bk to FT HPS = 1&quot;.
              </p>
              <p className="text-[11px] text-muted-foreground" data-testid="jacket-shoulder-curve-note-text">
                Shoulder seam curve controls default to 0,0. At 0,0 the curve handle sits at the segment midpoint so the seam stays straight.
              </p>
            </div>
          ),
        },
        waist: {
          title: 'Waist Advanced Controls',
          content: (
            <div className="space-y-3">
              {renderJacketInput('distanceToWaist', 'Distance to Waist')}
              <p className="text-[11px] text-muted-foreground" data-testid="jacket-waist-advanced-note">
                Controls vertical placement of waist level from upper body reference.
              </p>
            </div>
          ),
        },
        belly: {
          title: 'Belly Advanced Controls',
          content: (
            <div className="space-y-3">
              {renderJacketInput('distanceToBelly', 'Distance to Belly')}
              <p className="text-[11px] text-muted-foreground" data-testid="jacket-belly-advanced-note">
                Controls vertical placement of belly level from waist reference.
              </p>
            </div>
          ),
        },
        xBack: {
          title: 'X-Back Advanced Controls',
          content: renderJacketOverrideControl({
            section: 'xback',
            label: 'CB0→CB3/BXB Vertical Drop',
            enabledKey: 'overrideAcrossBackDepthEnabled',
            valueKey: 'overrideAcrossBackDepth',
            autoValue: matchingSummary.autoAcrossBackDepth,
            drawnValue: matchingSummary.resolvedAcrossBackDepth,
          }),
        },
        xFront: {
          title: 'X-Front Advanced Controls',
          content: renderJacketOverrideControl({
            section: 'xfront',
            label: 'CF0→CF1/FXF Vertical Drop',
            enabledKey: 'overrideAcrossFrontDepthEnabled',
            valueKey: 'overrideAcrossFrontDepth',
            autoValue: matchingSummary.autoAcrossFrontDepth,
            drawnValue: matchingSummary.resolvedAcrossFrontDepth,
          }),
        },
        elbow: {
          title: 'Elbow Advanced Controls',
          content: (
            <div className="space-y-3" data-testid="jacket-elbow-advanced-control-list">
              {renderJacketInput('sleeveDistToElbow', 'Distance to Elbow', {
                inputValue: Number(measurements?.sleeveDistToElbow || 0) > 0
                  ? measurements?.sleeveDistToElbow
                  : (matchingSummary?.sleeveDistToElbowAuto ?? matchingSummary?.sleeveDistToElbow ?? ''),
              })}
              <p className="text-[11px] text-muted-foreground" data-testid="jacket-sleeve-distance-to-elbow-note-text">
                Use 0 to keep the auto elbow depth. Any positive value locks SLT to elbow distance directly.
              </p>
            </div>
          ),
        },
        sleeve: {
          title: 'Sleeve Cap Advanced Controls',
          content: (
            <div className="space-y-3" data-testid="jacket-sleeve-advanced-control-list">
              {renderJacketInput('topSleeveEasePercent', 'Top Sleeve Ease %', { step: 1 })}
              {renderJacketInput('underSleeveEasePercent', 'Under Sleeve Ease %', { step: 1 })}
              <p className="text-[11px] text-muted-foreground" data-testid="jacket-sleeve-ease-distribution-note-text">
                Top Sleeve and Under Sleeve ease percentages are normalized together. Top Sleeve ease is split 50/50 between the back crown and front crown.
              </p>
            </div>
          ),
        },
      };

      const panel = panelMap[activeJacketAdvancedControl];
      if (!panel) return null;

      return (
        <AdvancedOverlayPanel
          title={panel.title}
          onClose={() => setActiveJacketAdvancedControl(null)}
          testIdPrefix="jacket-advanced-controls-side-panel"
        >
          {panel.content}
        </AdvancedOverlayPanel>
      );
    };

    return (
      <aside className={`w-[392px] flex-shrink-0 border-r border-slate-200 bg-[#fafafa] h-full flex flex-col ${!canEdit ? 'pointer-events-none select-none' : ''}`} data-testid="left-panel-jacket-garment">
        <div className="px-3 py-3 border-b border-slate-200 bg-white/90">
          <h2 className="text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-600 flex items-center gap-2">
            <Settings size={14} /> Jacket Base Block ({jacketDraftLabel})
          </h2>
          {!canEdit && (
            <p className="text-xs text-amber-700 mt-2 font-semibold" data-testid="left-panel-jacket-locked-text">LOCKED — Unlock to edit</p>
          )}
        </div>

        <ScrollArea className="flex-1">
          <div className="p-3 flex gap-2.5">
            <IconTabRail
              tabs={jacketTabs}
              activeTab={activeJacketPanel}
              onTabChange={setActiveJacketPanel}
              testId="jacket-panel-icon-rail"
              dense
            />

            <div className="flex-1 min-w-0">
              <Accordion type="single" collapsible value={activeJacketPanel} onValueChange={(value) => value && setActiveJacketPanel(value)} className="space-y-0">
                <AccordionItem value="body" className={activeJacketPanel === 'body' ? 'border border-slate-200 rounded-md bg-transparent px-2 shadow-sm' : 'hidden'} data-testid="jacket-body-accordion-item">
                  <AccordionTrigger className="hidden" />
                  <AccordionContent className="pt-1.5 pb-2 space-y-1.5" data-testid="jacket-panel-body-content">
                    {renderGeometryEditingPanel('jacket-geometry-edit-state-panel', {
                      showJacketPointHelp: true,
                      selectedPointCount: selectedFreePointsCount,
                      compact: true,
                      cadDense: true,
                    })}

                    {renderDrawnMeasurementPanel(jacketDrawnRows, 'jacket-drawn-measurements-panel', {
                      compact: true,
                      cadDense: true,
                      hideSeed: true,
                      hideHelperText: true,
                    })}

                    <div className="grid grid-cols-2 gap-1.5">
                      {renderJacketSelect('bellyProfile', 'Belly Profile')}
                      {renderJacketSelect('posture', 'Posture')}
                    </div>

                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="sleeve" className={activeJacketPanel === 'sleeve' ? 'border border-slate-200 rounded-2xl bg-transparent px-2.5 shadow-sm' : 'hidden'} data-testid="jacket-sleeve-accordion-item">
                  <AccordionTrigger className="hidden" />
                  <AccordionContent className="pt-2 pb-3 space-y-2.5" data-testid="jacket-panel-sleeve-content">
                    <div className="grid grid-cols-2 gap-3">
                      {renderJacketInput('jacketSleeveLength', 'Sleeve Length', {
                        drawnText: resolveJacketDrawnValueText('jacketSleeveLength'),
                      })}
                      {renderJacketInput('jacketSleeveCapEase', 'Sleeve Cap Ease', { advancedKey: 'sleeve' })}
                      {renderJacketInput('jacketSleeveBicep', 'Bicep (Full)', { drawnText: resolveJacketSleeveSplitText('bicep') })}
                      {renderJacketInput('jacketSleeveElbow', 'Elbow (Full)', {
                        advancedKey: 'elbow',
                        drawnText: resolveJacketSleeveSplitText('elbow'),
                      })}
                      {renderJacketInput('jacketSleeveHem', 'Hem (Full)', { drawnText: resolveJacketSleeveSplitText('hem') })}
                      {renderJacketInput('sleevePitchRotation', 'Pitch Rotation', {
                        step: 0.125,
                        inputValue: measurements?.sleevePitchRotation ?? measurements?.sleevePitchShift ?? 0,
                      })}
                    </div>

                    <div className="space-y-2 rounded-xl border border-slate-200 bg-white/90 p-2.5 shadow-sm" data-testid="jacket-sleeve-front-pitch-slider-wrap">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Front Pitch Height % of Cap</span>
                        <span className="font-semibold" data-testid="jacket-sleeve-front-pitch-slider-value-text">
                          {(Number(measurements.sleeveFrontPitchPercent || 0.7) * 100).toFixed(1)}%
                        </span>
                      </div>
                      <Slider
                        min={20}
                        max={80}
                        step={0.5}
                        value={[Number(measurements.sleeveFrontPitchPercent || 0.7) * 100]}
                        onValueChange={(values) => onMeasurementChange('sleeveFrontPitchPercent', (values[0] || 70) / 100)}
                        disabled={!canEdit}
                        data-testid="jacket-sleeve-front-pitch-slider"
                      />
                    </div>

                    <div className="space-y-2 rounded-xl border border-slate-200 bg-white/90 p-2.5 shadow-sm" data-testid="jacket-sleeve-back-pitch-slider-wrap">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Back Pitch Height % of Cap</span>
                        <span className="font-semibold" data-testid="jacket-sleeve-back-pitch-slider-value-text">
                          {(Number(measurements.sleeveBackPitchPercent || 0.35) * 100).toFixed(1)}%
                        </span>
                      </div>
                      <Slider
                        min={20}
                        max={75}
                        step={0.5}
                        value={[Number(measurements.sleeveBackPitchPercent || 0.35) * 100]}
                        onValueChange={(values) => onMeasurementChange('sleeveBackPitchPercent', (values[0] || 35) / 100)}
                        disabled={!canEdit}
                        data-testid="jacket-sleeve-back-pitch-slider"
                      />
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-white/90 p-2.5 text-[10px] leading-4 shadow-sm grid grid-cols-2 gap-x-3 gap-y-1.5" data-testid="jacket-sleeve-summary-box">
                      <p data-testid="jacket-sleeve-length-midpoint-text">Resolved sleeve length (SLT→mid HB-TSHF): <span className="font-semibold">{Number(matchingSummary?.jacketSleeveLengthMidpointDrawn || 0).toFixed(3)}&quot;</span></p>
                      <p data-testid="jacket-sleeve-depth-resolved-text">Resolved sleeve depth seed: <span className="font-semibold">{Number(matchingSummary?.jacketSleeveLengthDepthResolved || 0).toFixed(3)}&quot;</span></p>
                      <p data-testid="jacket-sleeve-cap-height-text">Solved cap height: <span className="font-semibold">{matchingSummary?.sleeveCapHeight ?? 0}&quot;</span></p>
                      <p data-testid="jacket-sleeve-cap-path-text">Sleeve cap path: <span className="font-semibold">{matchingSummary?.sleeveCapPathLength ?? 0}&quot;</span></p>
                      <p data-testid="jacket-sleeve-cap-target-text">Target (body armhole + ease): <span className="font-semibold">{matchingSummary?.sleeveCapTarget ?? 0}&quot;</span></p>
                      <p data-testid="jacket-sleeve-body-armhole-text">Body armhole: <span className="font-semibold">{Number(patternData?.sleeveMeta?.bodyArmhole || matchingSummary?.totalArmholeCurve || 0).toFixed(3)}&quot;</span></p>
                      <p data-testid="jacket-sleeve-distance-to-elbow-text">Distance to elbow: <span className="font-semibold">{matchingSummary?.sleeveDistToElbow ?? 0}&quot;</span></p>
                      <p data-testid="jacket-sleeve-pitch-rotation-text">Pitch rotation: <span className="font-semibold">{matchingSummary?.sleevePitchRotation ?? matchingSummary?.sleevePitchShift ?? 0}&quot;</span></p>
                      <p data-testid="jacket-sleeve-top-ease-percent-text">Top Sleeve ease: <span className="font-semibold">{matchingSummary?.topSleeveEasePercent ?? 0}%</span></p>
                      <p data-testid="jacket-sleeve-under-ease-percent-text">Under Sleeve ease: <span className="font-semibold">{matchingSummary?.underSleeveEasePercent ?? 0}%</span></p>
                      <p data-testid="jacket-sleeve-base-half-bicep-text">Base half bicep: <span className="font-semibold">{matchingSummary?.jacketSleeveBicepHalfBase ?? 0}&quot;</span></p>
                      <p data-testid="jacket-sleeve-base-half-elbow-text">Base half elbow: <span className="font-semibold">{matchingSummary?.jacketSleeveElbowHalfBase ?? 0}&quot;</span></p>
                      <p data-testid="jacket-sleeve-base-half-hem-text">Base half hem: <span className="font-semibold">{matchingSummary?.jacketSleeveHemHalfBase ?? 0}&quot;</span></p>
                      <p className="text-emerald-700 col-span-2" data-testid="jacket-sleeve-ts-front-seam-text">{resolveJacketFrontSeamText('ts')}</p>
                      <p className="text-emerald-700 col-span-2" data-testid="jacket-sleeve-us-front-seam-text">{resolveJacketFrontSeamText('us')}</p>
                    </div>

                    <p className="text-[10px] leading-4 text-slate-500" data-testid="jacket-sleeve-note-text">
                      Jacket sleeve V1 now solves sleeve length to the straight line from SLT to the midpoint of HB-TSHF, while still solving the cap to body armhole + ease and reporting TS/US split widths below each width input.
                    </p>
                    <p className="text-[10px] leading-4 text-slate-500" data-testid="jacket-sleeve-pitch-rotation-note-text">
                      Positive pitch rotation moves the shoulder match onto the front armhole; negative values move it toward the back armhole. The sleeve shoulder notch shifts on the top sleeve automatically.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="panel" className={activeJacketPanel === 'panel' ? 'border border-slate-200 rounded-2xl bg-transparent px-2.5 shadow-sm' : 'hidden'} data-testid="jacket-fish-dart-accordion-item">
                  <AccordionTrigger className="hidden" />
                  <AccordionContent className="pt-2 pb-3 space-y-2.5" data-testid="jacket-panel-fish-dart-content">
                    <div className="space-y-2 rounded-md border bg-muted/20 p-3" data-testid="jacket-front-dart-mode-box">
                      <p className="text-sm font-medium" data-testid="jacket-front-dart-mode-title-text">Front Dart Mode</p>
                      {renderJacketSelect('jacketDartMode', 'Dart Mode', {
                        optionMap: jacketDartModeLabelMap,
                        defaultValue: 'belly_and_fish',
                      })}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {renderJacketInput('dartWidthWaist', 'Dart Width at Waist', { readOnly: !hasFrontFishDart })}
                      {renderJacketInput('dartWidthBelly', 'Dart Width at Belly', { readOnly: !hasFrontBellyDart })}
                      {renderJacketInput('dartLengthTop', 'Dart Length Top', { readOnly: !hasFrontFishDart })}
                      {renderJacketInput('dartLengthBottom', 'Dart Length Bottom', { readOnly: !hasFrontFishDart })}
                      {renderJacketInput('dartWaistShift', 'Dart Waist Shift', { step: 0.125, readOnly: !hasFrontFishDart })}
                    </div>

                    <div className="rounded-md border bg-muted/20 p-3 text-xs space-y-1" data-testid="jacket-fish-dart-summary-box">
                      <p data-testid="jacket-fish-dart-summary-mode-text">
                        Mode: <span className="font-semibold">{jacketDartModeLabelMap[currentJacketDartMode]}</span>
                      </p>
                      <p data-testid="jacket-fish-dart-summary-raw-waist-text">
                        Front Waist Raw: <span className="font-semibold">{Number(matchingSummary?.frontWaistRaw || 0).toFixed(3)}&quot;</span>
                      </p>
                      <p data-testid="jacket-fish-dart-summary-intake-text">
                        Dart Intake (FD1-FD2): <span className="font-semibold">{Number(matchingSummary?.frontWaistDartIntake || 0).toFixed(3)}&quot;</span>
                      </p>
                      <p data-testid="jacket-fish-dart-summary-belly-intake-text">
                        Belly Dart (BD1-BD2): <span className="font-semibold">{Number(matchingSummary?.frontBellyDartIntake || 0).toFixed(3)}&quot;</span>
                      </p>
                      <p data-testid="jacket-fish-dart-summary-net-waist-text">
                        Front Waist Net: <span className="font-semibold">{Number(matchingSummary?.frontWaistNet || 0).toFixed(3)}&quot;</span>
                      </p>
                      <div className="pt-2" data-testid="jacket-front-side-seam-summary-box">
                        {renderJacketFrontSideSeamMatchInfo()}
                      </div>
                    </div>

                    <p className="text-[11px] text-muted-foreground" data-testid="jacket-fish-dart-note-text">
                      `Off` connects FWPS directly to FBPS. `Fish Dart` drafts the centered FD0→FD1→FD3→FD2 contour only. `Belly + Fish Dart` keeps the current side cut plus the centered fish dart.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="pocket" className={activeJacketPanel === 'pocket' ? 'border border-slate-200 rounded-2xl bg-transparent px-2.5 shadow-sm' : 'hidden'} data-testid="jacket-pocket-accordion-item">
                  <AccordionTrigger className="hidden" />
                  <AccordionContent className="pt-2 pb-3 space-y-3" data-testid="jacket-panel-pocket-content">
                    <div className="rounded-md border p-3 bg-muted/10 space-y-2" data-testid="jacket-pocket-header-box">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground" data-testid="jacket-pocket-title-text">Pocket Markers</p>
                      <p className="text-[11px] text-muted-foreground" data-testid="jacket-pocket-description-text">
                        Draft the Jacket chest pocket (`CPKT1-4`) and side pocket opening (`SPK1-2`) directly on the front panel.
                      </p>
                    </div>

                    <div className="space-y-3 rounded-md border p-3 bg-background" data-testid="jacket-chest-pocket-controls-box">
                      <div className="flex items-center justify-between" data-testid="jacket-chest-pocket-toggle-row">
                        <div>
                          <p className="text-sm font-medium">Chest Pocket</p>
                          <p className="text-[11px] text-muted-foreground">Angled 4-corner chest pocket outline above the chest line.</p>
                        </div>
                        <Switch
                          checked={isChestPocketEnabled}
                          onCheckedChange={(checked) => onMeasurementChange('chestPocketEnabled', checked)}
                          data-testid="jacket-chest-pocket-enabled-toggle-switch"
                        />
                      </div>

                      {isChestPocketEnabled && (
                        <div className="grid grid-cols-2 gap-3" data-testid="jacket-chest-pocket-input-grid">
                          {renderJacketInput('chestPocketWidth', 'Pocket Width')}
                          {renderJacketInput('chestPocketDepth', 'Pocket Height')}
                          {renderJacketInput('chestPocketAboveChest', 'Above Chest Line')}
                          {renderJacketInput('chestPocketArmholeLift', 'Armhole Lift')}
                        </div>
                      )}
                    </div>

                    <div className="space-y-3 rounded-md border p-3 bg-background" data-testid="jacket-side-pocket-controls-box">
                      <div className="flex items-center justify-between" data-testid="jacket-side-pocket-toggle-row">
                        <div>
                          <p className="text-sm font-medium">Side Pocket</p>
                          <p className="text-[11px] text-muted-foreground">Straight opening overlapping the FD4-BD1 seam reference.</p>
                        </div>
                        <Switch
                          checked={isSidePocketEnabled}
                          onCheckedChange={(checked) => onMeasurementChange('sidePocketEnabled', checked)}
                          data-testid="jacket-side-pocket-enabled-toggle-switch"
                        />
                      </div>

                      {isSidePocketEnabled && (
                        <div className="grid grid-cols-2 gap-3" data-testid="jacket-side-pocket-input-grid">
                          {renderJacketInput('sidePocketLength', 'Pocket Length')}
                          {renderJacketInput('sidePocketStartOffset', 'Start from FD4')}
                        </div>
                      )}
                    </div>

                    <div className="rounded-md border bg-muted/20 p-3 text-xs space-y-1" data-testid="jacket-pocket-summary-box">
                      <p data-testid="jacket-pocket-summary-chest-state-text">Chest pocket: <span className="font-semibold">{isChestPocketEnabled ? 'ON' : 'OFF'}</span></p>
                      <p data-testid="jacket-pocket-summary-chest-width-text">Chest width drawn: <span className="font-semibold">{Number(matchingSummary?.chestPocketWidthDrawn || 0).toFixed(3)}&quot;</span></p>
                      <p data-testid="jacket-pocket-summary-chest-depth-text">Chest height drawn: <span className="font-semibold">{Number(matchingSummary?.chestPocketDepthDrawn || 0).toFixed(3)}&quot;</span></p>
                      <p data-testid="jacket-pocket-summary-side-state-text">Side pocket: <span className="font-semibold">{isSidePocketEnabled ? 'ON' : 'OFF'}</span></p>
                      <p data-testid="jacket-pocket-summary-side-length-text">Side length drawn: <span className="font-semibold">{Number(matchingSummary?.sidePocketLengthDrawn || 0).toFixed(3)}&quot;</span></p>
                    </div>

                    <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-[11px] text-sky-900 space-y-1" data-testid="jacket-pocket-free-mode-note-box">
                      <p data-testid="jacket-pocket-free-mode-note-text">Free Mode tip: select `CPKT1-4` or `SPK1-2` on canvas, then use the floating nudge pad for local pocket shaping.</p>
                      <p data-testid="jacket-pocket-free-mode-selection-text">Current selection: <span className="font-semibold">{selectedFreePointsCount}</span> point(s)</p>
                      <p data-testid="jacket-pocket-free-mode-state-text">Geometry editing: <span className="font-semibold">ON</span></p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="button" className={activeJacketPanel === 'button' ? 'border border-slate-200 rounded-2xl bg-transparent px-2.5 shadow-sm' : 'hidden'} data-testid="jacket-button-accordion-item">
                  <AccordionTrigger className="hidden" />
                  <AccordionContent className="pt-2 pb-3 space-y-3" data-testid="jacket-panel-button-content">
                    <div className="rounded-md border p-3 bg-muted/10 space-y-2" data-testid="jacket-button-header-box">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground" data-testid="jacket-button-title-text">Front Button Plan</p>
                      <p className="text-[11px] text-muted-foreground" data-testid="jacket-button-description-text">
                        Add standard jacket buttons on the front panel and keep the same markers in print preview / PDF.
                      </p>
                    </div>

                    <div className="space-y-3 rounded-md border p-3 bg-background" data-testid="jacket-button-controls-box">
                      {!isDoubleBreastedClosure && renderJacketSelect('jacketButtonLayout', 'Button Layout', {
                        optionMap: jacketButtonLayoutLabelMap,
                        defaultValue: 'two_buttons',
                      })}

                      {(hasLowerJacketButton || isDoubleBreastedClosure) && (
                        <div className="grid grid-cols-2 gap-3" data-testid="jacket-button-lower-input-grid">
                          {renderJacketInput('jacketLowerButtonDistance', 'Lower Button Drop')}
                        </div>
                      )}

                      {hasUpperJacketButton && (
                        <div className="grid grid-cols-2 gap-3" data-testid="jacket-button-upper-input-grid">
                          {renderJacketInput('jacketUpperButtonDistance', 'Upper Roll Drop')}
                        </div>
                      )}

                      <div className="rounded-md border bg-muted/20 p-3 text-[11px] text-muted-foreground space-y-1" data-testid="jacket-button-default-note-box">
                        {isDoubleBreastedClosure ? (
                          <>
                            <p data-testid="jacket-button-default-note-text">Double-breasted front uses a fixed 5-button plan on the front panel.</p>
                            <p data-testid="jacket-button-edge-offset-note-text">Top and lower pairs sit `0.5&quot;` and `4&quot;` away from the straight front edge, and every rendered button can be selected on canvas for individual or grouped nudging.</p>
                          </>
                        ) : (
                          <>
                            <p data-testid="jacket-button-default-note-text">Primary button stays at `CF3`.</p>
                            <p data-testid="jacket-button-edge-offset-note-text">Every rendered button can be selected on canvas for individual or grouped nudging, and its buttonhole moves with it.</p>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="rounded-md border bg-muted/20 p-3 text-xs space-y-1" data-testid="jacket-button-summary-box">
                      <p data-testid="jacket-button-summary-layout-text">Layout: <span className="font-semibold">{isDoubleBreastedClosure ? 'Double Breasted · Fixed 5 Buttons' : jacketButtonLayoutLabelMap[jacketButtonLayout]}</span></p>
                      <p data-testid="jacket-button-summary-count-text">Resolved buttons: <span className="font-semibold">{Number(matchingSummary?.jacketButtonCount || 0)}</span></p>
                      <p data-testid="jacket-button-summary-buttonhole-text">Buttonhole length: <span className="font-semibold">{Number(matchingSummary?.jacketButtonholeLength || 0).toFixed(3)}&quot;</span></p>
                      {(hasLowerJacketButton || isDoubleBreastedClosure) && (
                        <p data-testid="jacket-button-summary-lower-distance-text">Lower button drop: <span className="font-semibold">{Number(matchingSummary?.jacketLowerButtonDistance || measurements?.jacketLowerButtonDistance || 0).toFixed(3)}&quot;</span></p>
                      )}
                      {hasUpperJacketButton && (
                        <p data-testid="jacket-button-summary-upper-distance-text">Upper roll drop: <span className="font-semibold">{Number(matchingSummary?.jacketUpperButtonDistance || measurements?.jacketUpperButtonDistance || 0).toFixed(3)}&quot;</span></p>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="lapel" className={activeJacketPanel === 'lapel' ? 'border border-slate-200 rounded-2xl bg-transparent px-2.5 shadow-sm' : 'hidden'} data-testid="jacket-lapel-accordion-item">
                  <AccordionTrigger className="hidden" />
                  <AccordionContent className="pt-2 pb-3 space-y-2.5" data-testid="jacket-panel-lapel-content">
                    <div className="rounded-md border p-3 bg-muted/10 space-y-2" data-testid="jacket-lapel-header-box">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground" data-testid="jacket-lapel-title-text">Lapel Controls</p>
                      <p className="text-[11px] text-muted-foreground" data-testid="jacket-lapel-description-text">
                        Switch lapel type and front closure while keeping the same jacket body block.
                      </p>
                    </div>

                    {renderJacketSelect('jacketFrontClosure', 'Front Closure', {
                      optionMap: jacketFrontClosureLabelMap,
                      defaultValue: 'single_breasted',
                    })}

                    <div className="space-y-1.5" data-testid="jacket-lapel-type-row">
                      <Label className="text-[11px] text-muted-foreground">Lapel Type</Label>
                      <Select
                        value={activeJacketLapelType}
                        onValueChange={(nextValue) => onMeasurementChange('lapelType', nextValue)}
                        disabled={!canEdit}
                      >
                        <SelectTrigger data-testid="jacket-lapel-type-select-trigger">
                          <SelectValue placeholder="Select lapel type" />
                        </SelectTrigger>
                        <SelectContent data-testid="jacket-lapel-type-select-content">
                          {lapelTypeOptions.map((option) => (
                            <SelectItem
                              key={option.value}
                              value={option.value}
                              disabled={!option.enabled || !canEdit}
                              data-testid={`jacket-lapel-type-option-${option.value}`}
                            >
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-3" data-testid="jacket-lapel-basic-input-grid">
                      {renderJacketInput('lapelWidth', 'Lapel Width', {
                        step: 0.0625,
                        min: 1.25,
                        max: 5.5,
                      })}
                      {renderJacketInput('gorgePointDepth', 'Gorge Point', {
                        step: 0.0625,
                        min: 1.5,
                        max: 8,
                      })}
                    </div>

                    <div className="rounded-md border border-sky-200 bg-sky-50 p-3" data-testid="jacket-lapel-summary-box">
                      <p className="text-xs font-semibold text-sky-800" data-testid="jacket-lapel-summary-title-text">{lapelSummaryTitleMap[lapelSummary?.lapelType || 'pointed_notch'] || 'Current Lapel Summary'}</p>
                      <div className="mt-2 space-y-1 text-[11px] text-sky-900" data-testid="jacket-lapel-summary-values-wrap">
                        <p data-testid="jacket-lapel-summary-type-text">Type: {(lapelSummary?.lapelType || 'pointed_notch').replaceAll('_', ' ')}</p>
                        <p data-testid="jacket-lapel-summary-closure-text">Front closure: {jacketFrontClosureLabelMap[activeJacketFrontClosure] || 'Single Breasted'}</p>
                        <p data-testid="jacket-lapel-summary-width-text">Width: {formatMeasurement(lapelSummary?.lapelWidth || measurements?.lapelWidth || 0)}&quot;</p>
                        <p data-testid="jacket-lapel-summary-gorge-depth-text">LP1 Distance on Roll Line: {formatMeasurement(lapelSummary?.gorgePointDepth || measurements?.gorgePointDepth || 0)}&quot;</p>
                        {!isShawlFamilyLapelSummary && (
                          <>
                            <p data-testid="jacket-lapel-summary-notch-pitch-text">GP from LP0: {formatMeasurement(lapelSummary?.gpDistanceFromLp0 || 0)}&quot;</p>
                            <p data-testid="jacket-lapel-summary-cp0-length-text">GP-CP0 Length: {formatMeasurement(lapelSummary?.cp0SegmentLength || 0)}&quot;</p>
                          </>
                        )}
                        {!isShawlFamilyLapelSummary && (
                          <p data-testid="jacket-lapel-summary-buttonhole-length-text">LB0-LB1 Length: {formatMeasurement(lapelSummary?.buttonholeLength || 0)}&quot;</p>
                        )}
                        {(lapelSummaryDetailMap[lapelSummary?.lapelType || 'pointed_notch'] || []).map((item) => (
                          <p key={item.key} data-testid={`jacket-lapel-summary-${item.key}-text`}>
                            {item.label}: {formatMeasurement(item.value || 0)}&quot;
                          </p>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-md border p-3 bg-background" data-testid="jacket-lapel-formula-box">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide" data-testid="jacket-lapel-formula-title-text">Current Lapel Formula Data</p>
                      <div className="mt-2 space-y-1" data-testid="jacket-lapel-formula-lines-wrap">
                        {(lapelSummary?.formulaLines || []).map((line, index) => (
                          <p key={`${line}-${index}`} className="text-[11px] text-muted-foreground" data-testid={`jacket-lapel-formula-line-${index}-text`}>
                            {line}
                          </p>
                        ))}
                      </div>
                    </div>

                    {isDoubleBreastedClosure && (
                      <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-[11px] text-sky-900 space-y-1" data-testid="jacket-double-breasted-nudge-note-box">
                        <p data-testid="jacket-double-breasted-nudge-note-text">All double-breasted buttons are directly selectable on canvas. Click one or lasso multiple buttons, then use the nudge pad to move the buttons and buttonholes together.</p>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="formula" className={activeJacketPanel === 'formula' ? 'border border-slate-200 rounded-2xl bg-transparent px-2.5 shadow-sm' : 'hidden'} data-testid="jacket-formula-accordion-item">
                  <AccordionTrigger className="hidden" />
                  <AccordionContent className="pt-2 pb-3 space-y-2.5" data-testid="jacket-panel-formula-content">
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={onResetCurveHandles}
                        disabled={!canEdit}
                        data-testid="jacket-reset-curve-handles-button"
                      >
                        Reset Curve Handles
                      </Button>
                    </div>

                    <p className="text-[11px] text-muted-foreground" data-testid="jacket-curve-edit-instruction-text">
                      Curve edit flow is same as Shirt: click a curve segment on canvas, then drag handles.
                    </p>

                    {pointReferences.length > 0 ? (
                      <Accordion type="multiple" className="space-y-2" data-testid="jacket-geometry-description-accordion">
                        <AccordionItem value="jacket-geometry-front" className="rounded-md border px-3" data-testid="jacket-geometry-front-accordion-item">
                          <AccordionTrigger className="py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Front</AccordionTrigger>
                          <AccordionContent className="space-y-2 pb-3" data-testid="jacket-geometry-front-accordion-content">
                            {jacketFormulaSections.front.map((item, index) => (
                              <p key={`front-${index}`} className="text-xs leading-relaxed text-muted-foreground" data-testid={`jacket-geometry-front-line-${index}-text`}>
                                • {item}
                              </p>
                            ))}
                          </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="jacket-geometry-side" className="rounded-md border px-3" data-testid="jacket-geometry-side-accordion-item">
                          <AccordionTrigger className="py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Side</AccordionTrigger>
                          <AccordionContent className="space-y-2 pb-3" data-testid="jacket-geometry-side-accordion-content">
                            {jacketFormulaSections.side.map((item, index) => (
                              <p key={`side-${index}`} className="text-xs leading-relaxed text-muted-foreground" data-testid={`jacket-geometry-side-line-${index}-text`}>
                                • {item}
                              </p>
                            ))}
                          </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="jacket-geometry-belly-profile" className="rounded-md border px-3" data-testid="jacket-geometry-belly-profile-accordion-item">
                          <AccordionTrigger className="py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Belly Profile</AccordionTrigger>
                          <AccordionContent className="space-y-2 pb-3" data-testid="jacket-geometry-belly-profile-accordion-content">
                            {jacketFormulaSections.bellyProfile.map((item, index) => (
                              <p key={`belly-profile-${index}`} className="text-xs leading-relaxed text-muted-foreground" data-testid={`jacket-geometry-belly-profile-line-${index}-text`}>
                                • {item}
                              </p>
                            ))}
                          </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="jacket-geometry-back" className="rounded-md border px-3" data-testid="jacket-geometry-back-accordion-item">
                          <AccordionTrigger className="py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Back</AccordionTrigger>
                          <AccordionContent className="space-y-2 pb-3" data-testid="jacket-geometry-back-accordion-content">
                            {jacketFormulaSections.back.map((item, index) => (
                              <p key={`back-${index}`} className="text-xs leading-relaxed text-muted-foreground" data-testid={`jacket-geometry-back-line-${index}-text`}>
                                • {item}
                              </p>
                            ))}
                          </AccordionContent>
                        </AccordionItem>

                        <AccordionItem value="jacket-geometry-sleeve" className="rounded-md border px-3" data-testid="jacket-geometry-sleeve-accordion-item">
                          <AccordionTrigger className="py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sleeve</AccordionTrigger>
                          <AccordionContent className="space-y-2 pb-3" data-testid="jacket-geometry-sleeve-accordion-content">
                            {jacketFormulaSections.sleeve.map((item, index) => (
                              <p key={`sleeve-${index}`} className="text-xs leading-relaxed text-muted-foreground" data-testid={`jacket-geometry-sleeve-line-${index}-text`}>
                                • {item}
                              </p>
                            ))}
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    ) : (
                      <p className="text-xs text-muted-foreground" data-testid="jacket-point-reference-empty">Update any input to generate point references.</p>
                    )}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

          </div>
        </ScrollArea>

        <div className="p-3 border-t bg-muted/10 text-xs text-muted-foreground" data-testid="jacket-left-panel-footer">
          <div className="flex items-center justify-between">
            <span>Block: <span className="text-emerald-600 font-semibold">BASE BLOCK · FRONT/SIDE/BACK/LAPEL</span></span>
            <span>Status: {garmentStatus.toUpperCase()}</span>
          </div>
        </div>

        {renderJacketAdvancedPanel()}
      </aside>
    );
  }

  if (garmentType !== 'trouser') {
    return (
      <aside className={`w-[420px] flex-shrink-0 border-r bg-card h-full flex flex-col shadow-lg ${!canEdit ? 'pointer-events-none select-none' : ''}`} data-testid="left-panel-planned-garment">
        <div className="p-4 border-b bg-muted/10">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <Settings size={14} /> Pattern Parameters
          </h2>
        </div>

        <div className="p-6 space-y-4">
          <div className="text-sm font-semibold" data-testid="planned-garment-title">{prettyGarmentType} Drafting</div>
          <p className="text-sm text-muted-foreground" data-testid="planned-garment-message">
            Formula files for {prettyGarmentType} are scaffolded and ready for iterative drafting.
            The active parametric draft currently available is Trouser.
          </p>
          <div className="text-xs text-muted-foreground" data-testid="planned-garment-status">
            Status: {garmentStatus.toUpperCase()}
          </div>
        </div>
      </aside>
    );
  }

  const resolvedBackDartMode = (() => {
    if (measurements?.backDartMode === 'none' || measurements?.backDartMode === 'one' || measurements?.backDartMode === 'two') {
      return measurements.backDartMode;
    }
    return measurements?.useTwoBackDarts ? 'two' : 'one';
  })();

  const frontWaistResolved = Number((Number(measurements?.waist || 0) / 4) + Number(measurements?.frontWaistAdd || 0));
  const backWaistResolved = Number((Number(measurements?.waist || 0) / 4) + Number(measurements?.backWaistAdd || 0));
  const frontHipResolved = Number((Number(measurements?.seat || 0) / 4) + Number(measurements?.frontHipAdd || 0));
  const backHipResolved = Number((Number(measurements?.seat || 0) / 4) + Number(measurements?.backHipAdd || 0));
  const isTrouserGeometryV4 = String(patternData?.formulaVersion || '').toLowerCase() === 'siy_v4'
    || String(patternData?.geometryVersion || '').toLowerCase() === 'trouser_geom_v4';
  const resolvedFrontKnee = (() => {
    const current = Number(measurements?.frontKnee);
    if (Number.isFinite(current)) return current;
    return Number(measurements?.knee || 0) / 2 - 1;
  })();
  const safeFrontKnee = Math.max(0.125, Math.min(Math.max(0.125, Number(measurements?.knee || 0) - 0.125), resolvedFrontKnee));
  const backKneeResolved = Number(measurements?.knee || 0) - safeFrontKnee;

  const resolvedFrontHem = (() => {
    const current = Number(measurements?.frontHem);
    if (Number.isFinite(current)) return current;
    return Number(measurements?.hem || 0) / 2 - (isTrouserGeometryV4 ? 0.75 : 1);
  })();
  const safeFrontHem = Math.max(0.125, Math.min(Math.max(0.125, Number(measurements?.hem || 0) - 0.125), resolvedFrontHem));
  const backHemResolved = Number(measurements?.hem || 0) - safeFrontHem;
  const trouserLengthMode = normalizeTrouserLengthMode(measurements?.trouserLengthMode);
  const isTrouserShortMode = trouserLengthMode === 'short';

  const trouserSummary = patternData?.matchingSummary || {};

  const formatTrouserValue = (value, digits = 3) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '—';
    return numeric.toFixed(digits).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
  };

  const trouserGeometryFrontLines = [
    `Front panel spine: FWC sits on the center-front waist, FCT is the front crotch tip, and the crease line runs straight through FCLW → FCLH for balancing the front leg visually on grain.`,
    `Front waist is split by FWC → FWS, with FCP acting as the chest-to-waist style transition point on the side seam. Current front waist resolved value is ${formatTrouserValue(frontWaistResolved)}" and the front hip resolved value is ${formatTrouserValue(frontHipResolved)}".`,
    `The front side seam builds in two stages: FWS → FHS through the Front Waist→Hip Side curve, then FHS → FCS through the Front Seat→Thigh Side curve. This keeps seat shaping separate from the thigh fall.`,
    `Front thigh width is controlled by fork width, front thigh side offset, and the inseam relationship between FCT and FKR. Current front thigh draw is ${formatTrouserValue(trouserSummary.frontThighDrawn)}" against a total thigh target of ${formatTrouserValue(measurements?.thigh)}".`,
    isTrouserShortMode
      ? `In Shorts mode, the lower front bypasses the knee entirely: the outer leg runs directly FCS → FHL and the inseam runs directly FHR → FCT. Outseam remains full length, while the inseam is interpreted from crotch to hem.`
      : `Below the thigh, the outer leg uses FCS → FKL through a cubic side curve before running to FHL at hem level. The inseam mirrors this with FKR → FCT and preserves the fork balance into the seat.`,
    isTrouserShortMode
      ? `Shorts mode suppresses knee drafting and keeps the hem split live. Current front hem is ${formatTrouserValue(safeFrontHem)}", while the back absorbs the remaining hem circumference.`
      : `Front knee and hem are independently split from the total circumference. The live resolved front knee is ${formatTrouserValue(safeFrontKnee)}" and front hem is ${formatTrouserValue(safeFrontHem)}", while the back absorbs the remaining circumference.`,
    `If pleats are enabled, pleat construction lines PL1A/PL1B (and PL2A/PL2B for two pleats) are drafted only on the front panel and do not alter the back block directly.`,
    isTrouserShortMode
      ? `Current named front geometry points available on canvas are FWC, FWS, FCP, FHS, FHC, FCS, FCT, FHL, FHR, FCLW, and FCLH.`
      : `Current named front geometry points available on canvas are FWC, FWS, FCP, FHS, FHC, FCS, FCT, FKL, FKR, FHL, FHR, FCLW, and FCLH.`,
  ];

  const trouserGeometryBackLines = [
    isTrouserGeometryV4
      ? `Back panel anchor points are BWC at the center-back waist, BHC at the center-back seat, BHS at the side seat, and BCT at the back crotch tip. In V4, the BHC → BWC segment also has a neutral center handle so you can introduce shape there without deforming existing drafts by default.`
      : `Back panel anchor points are BWC at the center-back waist, BHC at the center-back seat, BHS at the side seat, and BCT at the back crotch tip. These points define the seat swing and back balance.`,
    `Back waist shaping runs BWC → BWS, then the side seam continues BWS → BHS through the Back Waist→Hip Side curve. Current back waist resolved value is ${formatTrouserValue(backWaistResolved)}" and back hip resolved value is ${formatTrouserValue(backHipResolved)}".`,
    `The back seat is drafted through BHC and BHS first, then carried down toward BCS. This is why CB Seat X / Y and Side Thigh Offset are treated as separate controls: BHC/BHS govern seat placement, while BCS controls the thigh-side fall below the seat.`,
    `Back thigh shaping is built from BHS → BCS and then BCS → BKL using a cubic side curve. Current back thigh draw is ${formatTrouserValue(trouserSummary.backThighDrawn)}".`,
    `The back inseam uses BKR → BCT and the back crotch curve uses BCT → BHC. That crotch curve is the main balance line that controls extension, scoop, and how the back panel wraps under the seat.`,
    isTrouserShortMode
      ? `In Shorts mode, the lower back also bypasses the knee: BCS joins directly to BHL and the inseam closes directly from BHR to BCT. Back hem continues to auto-balance against the front split and is currently ${formatTrouserValue(backHemResolved)}".`
      : `Back knee and hem are auto-balanced against the front split. Current resolved back knee is ${formatTrouserValue(backKneeResolved)}" and back hem is ${formatTrouserValue(backHemResolved)}".`,
    `Back darts are drafted from the waist only. In 1-dart mode the geometry uses BD1 / BD2 / BDT1, and in 2-dart mode it adds BD3 / BD4 / BDT2 so total dart suppression can be distributed without changing the leg draft below waist level.`,
    isTrouserShortMode
      ? `Current named back geometry points available on canvas are BWC, BWS, BHS, BHC, BCS, BCT, BHL, BHR, BCLW, and BCLH, plus dart points when darts are enabled.`
      : `Current named back geometry points available on canvas are BWC, BWS, BHS, BHC, BCS, BCT, BKL, BKR, BHL, BHR, BCLW, and BCLH, plus dart points when darts are enabled.`,
  ];

  const supportsTrouserShorts = isTrouserGeometryV4;

  const resolveTrouserDrawnValueText = (id) => {
    if (!patternData || garmentType !== 'trouser') return null;

    if (id === 'waist' && trouserSummary.waistDrawnNetFull !== undefined) {
      const deviation = formatDeviation(measurements?.waist, trouserSummary.waistDrawnNetFull);
      return `Drawn: ${trouserSummary.waistDrawnNetFull}"${deviation ? ` | Δ ${deviation}"` : ''}`;
    }

    if (id === 'seat' && trouserSummary.seatDrawnFull !== undefined) {
      const deviation = formatDeviation(measurements?.seat, trouserSummary.seatDrawnFull);
      return `Drawn: ${trouserSummary.seatDrawnFull}"${deviation ? ` | Δ ${deviation}"` : ''}`;
    }

    if (id === 'thigh' && trouserSummary.thighDrawn !== undefined) {
      const deviation = formatDeviation(measurements?.thigh, trouserSummary.thighDrawn);
      return `Drawn: ${trouserSummary.thighDrawn}"${deviation ? ` | Δ ${deviation}"` : ''}`;
    }

    if (id === 'knee' && trouserSummary.kneeDrawn !== undefined) {
      if (isTrouserShortMode) return null;
      const deviation = formatDeviation(measurements?.knee, trouserSummary.kneeDrawn);
      return `Drawn: ${trouserSummary.kneeDrawn}"${deviation ? ` | Δ ${deviation}"` : ''}`;
    }

    if (id === 'hem' && trouserSummary.hemDrawn !== undefined) {
      const deviation = formatDeviation(measurements?.hem, trouserSummary.hemDrawn);
      return `Drawn: ${trouserSummary.hemDrawn}"${deviation ? ` | Δ ${deviation}"` : ''}`;
    }

    if (id === 'rise' && trouserSummary.frontRiseDrawn !== undefined) {
      return `Drawn F/B: ${trouserSummary.frontRiseDrawn}" / ${trouserSummary.backRiseDrawn}"`;
    }

    if (id === 'outseam' && trouserSummary.frontOutseamDrawn !== undefined) {
      return `Drawn F/B: ${trouserSummary.frontOutseamDrawn}" / ${trouserSummary.backOutseamDrawn}"`;
    }

    return null;
  };

  const trouserDrawnRows = [
    { key: 'waist', label: 'Waist', drawnValue: trouserSummary.waistDrawnNetFull, seedValue: referenceValueMap.waist, advancedKey: 'waist', onOpenAdvanced: setActiveTrouserAdvancedControl },
    { key: 'seat', label: 'Hip/Seat', drawnValue: trouserSummary.seatDrawnFull, seedValue: referenceValueMap.seat, advancedKey: 'hip', onOpenAdvanced: setActiveTrouserAdvancedControl },
    { key: 'rise', label: 'Front Rise', drawnValue: trouserSummary.frontRiseDrawn, seedValue: referenceValueMap.rise },
    { key: 'outseam', label: 'Outseam', drawnValue: trouserSummary.frontOutseamDrawn, seedValue: referenceValueMap.outseam },
    { key: 'thigh', label: 'Thigh', drawnValue: trouserSummary.thighDrawn, seedValue: referenceValueMap.thigh, advancedKey: 'thigh', onOpenAdvanced: setActiveTrouserAdvancedControl },
    ...(!isTrouserShortMode ? [{ key: 'knee', label: 'Knee', drawnValue: trouserSummary.kneeDrawn, seedValue: referenceValueMap.knee, advancedKey: 'knee', onOpenAdvanced: setActiveTrouserAdvancedControl }] : []),
    { key: 'hem', label: 'Hem', drawnValue: trouserSummary.hemDrawn, seedValue: referenceValueMap.hem, advancedKey: 'hem', onOpenAdvanced: setActiveTrouserAdvancedControl },
  ];

  const renderTrouserAdvancedPanel = () => {
    if (!activeTrouserAdvancedControl) return null;

    const panelMap = {
      waist: {
        title: 'Waist Sub Options',
        content: (
          <div className="space-y-3">
            {renderTrouserInput('cfDrop', 'CF Drop', measurements.cfDrop)}

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Front Waist</Label>
              <Input
                type="number"
                step="0.125"
                {...getBufferedInputHandlers('frontWaistResolved', frontWaistResolved.toFixed(3), {
                  commitOnBlurOnly: true,
                  commitKey: 'frontWaistResolved',
                })}
                disabled={!canEdit}
                data-testid="trouser-front-waist-resolved-input"
                className={`h-9 ${isModified('frontWaistAdd') ? 'border-orange-400 bg-orange-50/30' : ''} ${!canEdit ? 'bg-muted cursor-not-allowed' : ''}`}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Back Waist</Label>
              <Input
                type="number"
                step="0.125"
                value={backWaistResolved.toFixed(3)}
                readOnly
                disabled
                data-testid="trouser-back-waist-resolved-input"
                className="h-9 bg-muted cursor-not-allowed"
              />
            </div>

            <p className="text-[11px] text-muted-foreground" data-testid="trouser-waist-suboptions-note-text">
              Front Waist is editable. Back Waist is auto-balanced so total waist split remains constant.
            </p>
          </div>
        ),
      },
      hip: {
        title: 'Hip Sub Options',
        content: (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Front Hip</Label>
              <Input
                type="number"
                step="0.125"
                {...getBufferedInputHandlers('frontHipResolved', frontHipResolved.toFixed(3), {
                  commitOnBlurOnly: true,
                  commitKey: 'frontHipResolved',
                })}
                disabled={!canEdit}
                data-testid="trouser-front-hip-resolved-input"
                className={`h-9 ${isModified('frontHipAdd') ? 'border-orange-400 bg-orange-50/30' : ''} ${!canEdit ? 'bg-muted cursor-not-allowed' : ''}`}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Back Hip</Label>
              <Input
                type="number"
                step="0.125"
                value={backHipResolved.toFixed(3)}
                readOnly
                disabled
                data-testid="trouser-back-hip-resolved-input"
                className="h-9 bg-muted cursor-not-allowed"
              />
            </div>

            {renderTrouserInput('hipLevelInput', 'Hip Level', measurements.hipLevelInput)}

            <p className="text-[11px] text-muted-foreground" data-testid="trouser-hip-suboptions-note-text">
              Front Hip is editable in 0.125" steps. Back Hip auto-balances to keep total hip split constant.
            </p>
          </div>
        ),
      },
      thigh: {
        title: 'Thigh Sub Options',
        content: (
          <div className="space-y-3">
            {renderTrouserInput('forkWidth', 'Fork Width', measurements.forkWidth)}

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Front Thigh (Calculated)</Label>
              <Input
                type="number"
                step="0.125"
                value={Number(trouserSummary.frontThighDrawn ?? 0).toFixed(3)}
                readOnly
                disabled
                data-testid="trouser-front-thigh-calculated-input"
                className="h-9 bg-muted cursor-not-allowed"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Back Thigh (Calculated)</Label>
              <Input
                type="number"
                step="0.125"
                value={Number(trouserSummary.backThighDrawn ?? 0).toFixed(3)}
                readOnly
                disabled
                data-testid="trouser-back-thigh-calculated-input"
                className="h-9 bg-muted cursor-not-allowed"
              />
            </div>
          </div>
        ),
      },
      knee: {
        title: 'Knee Sub Options',
        content: (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Front Knee</Label>
              <Input
                type="number"
                step="0.125"
                {...getBufferedInputHandlers('frontKneeResolved', safeFrontKnee.toFixed(3), {
                    commitOnBlurOnly: true,
                  commitKey: 'frontKneeResolved',
                })}
                disabled={!canEdit}
                data-testid="trouser-front-knee-resolved-input"
                className={`h-9 ${isModified('frontKnee') ? 'border-orange-400 bg-orange-50/30' : ''} ${!canEdit ? 'bg-muted cursor-not-allowed' : ''}`}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Back Knee</Label>
              <Input
                type="number"
                step="0.125"
                value={backKneeResolved.toFixed(3)}
                readOnly
                disabled
                data-testid="trouser-back-knee-resolved-input"
                className="h-9 bg-muted cursor-not-allowed"
              />
            </div>

            {renderTrouserInput('kneeLevelInput', 'Knee Level', measurements.kneeLevelInput)}

            <p className="text-[11px] text-muted-foreground" data-testid="trouser-knee-suboptions-note-text">
              Front Knee is editable in 0.125" steps. Back Knee auto-balances to keep total knee circumference constant.
            </p>
          </div>
        ),
      },
      hem: {
        title: 'Hem Sub Options',
        content: (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Front Hem</Label>
              <Input
                type="number"
                step="0.125"
                {...getBufferedInputHandlers('frontHemResolved', safeFrontHem.toFixed(3), {
                    commitOnBlurOnly: true,
                  commitKey: 'frontHemResolved',
                })}
                disabled={!canEdit}
                data-testid="trouser-front-hem-resolved-input"
                className={`h-9 ${isModified('frontHem') ? 'border-orange-400 bg-orange-50/30' : ''} ${!canEdit ? 'bg-muted cursor-not-allowed' : ''}`}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Back Hem</Label>
              <Input
                type="number"
                step="0.125"
                value={backHemResolved.toFixed(3)}
                readOnly
                disabled
                data-testid="trouser-back-hem-resolved-input"
                className="h-9 bg-muted cursor-not-allowed"
              />
            </div>

            {isTrouserGeometryV4 && (
              <div className="rounded-md border bg-muted/20 p-3 space-y-3" data-testid="trouser-hem-shoe-shape-controls">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground">Shoe Shape</Label>
                  <Checkbox
                    checked={Boolean(measurements.hemShoeShapeEnabled)}
                    onCheckedChange={(checked) => onMeasurementChange('hemShoeShapeEnabled', checked === true)}
                    disabled={!canEdit}
                    data-testid="trouser-hem-shoe-shape-checkbox"
                  />
                </div>

                <div className="space-y-1.5" data-testid="trouser-hem-step-value-wrap">
                  <Label className="text-xs text-muted-foreground">Step Value</Label>
                  <Input
                    type="number"
                    step="0.125"
                    {...getBufferedInputHandlers('hemStepValue', measurements.hemStepValue, {
                      commitOnBlurOnly: true,
                      commitKey: 'hemStepValue',
                    })}
                    disabled={!canEdit}
                    data-testid="trouser-hemStepValue-input"
                    className={`h-9 ${isModified('hemStepValue') ? 'border-orange-400 bg-orange-50/30' : ''} ${!canEdit ? 'bg-muted cursor-not-allowed' : ''}`}
                  />
                </div>
              </div>
            )}

            <p className="text-[11px] text-muted-foreground" data-testid="trouser-hem-suboptions-note-text">
              Front Hem is editable in 0.125" steps. Back Hem auto-balances to keep total hem circumference constant.
            </p>
          </div>
        ),
      },
    };

    const panel = panelMap[activeTrouserAdvancedControl];
    if (!panel) return null;

    return (
      <div
        className="fixed left-[408px] top-24 z-50 w-[340px] rounded-xl border bg-background shadow-xl"
        data-testid="trouser-advanced-controls-side-panel"
      >
        <div className="flex items-center justify-between border-b px-4 py-3">
          <p className="text-sm font-semibold" data-testid="trouser-advanced-controls-side-panel-title">{panel.title}</p>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setActiveTrouserAdvancedControl(null)}
            data-testid="trouser-advanced-controls-side-panel-close-button"
          >
            <X size={14} />
          </Button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-4" data-testid="trouser-advanced-controls-side-panel-content">
          {panel.content}
        </div>
      </div>
    );
  };

  return (
    <aside className={`w-[392px] flex-shrink-0 border-r bg-card h-full flex flex-col shadow-lg ${!canEdit ? 'pointer-events-none select-none' : ''}`} data-testid="left-panel-trouser-garment">
      {/* Header */}
      <div className="p-3 border-b bg-muted/10">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
          <Settings size={14} /> Pattern Parameters
        </h2>
        {!canEdit && (
          <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
            <span className="font-semibold">LOCKED</span> - Unlock this pattern before editing
          </p>
        )}
      </div>

      {/* Scrollable Parameters */}
      <ScrollArea className="flex-1">
        <div className="p-2 flex gap-2">
          <IconTabRail
            tabs={trouserPanelTabs}
            activeTab={activeTrouserPanel}
            onTabChange={setActiveTrouserPanel}
            testId="trouser-panel-icon-rail"
          />

          <div className="flex-1 min-w-0">
          <Accordion type="single" collapsible value={activeTrouserPanel} onValueChange={(value) => value && setActiveTrouserPanel(value)} className="space-y-0">
            
            {/* Base Measurements */}
            <AccordionItem value="base" className={activeTrouserPanel === 'base' ? 'border rounded-md bg-card px-2' : 'hidden'} data-testid="trouser-base-accordion-item">
              <AccordionTrigger className="hidden">
                <div className="flex items-center gap-2">
                  <Ruler size={16} className="text-primary"/>
                  <span className="font-semibold text-sm">Base Measurements</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-1.5 pb-2 space-y-1.5" data-testid="trouser-base-accordion-content">
                {renderGeometryEditingPanel('trouser-geometry-edit-state-panel', {
                  cadDense: true,
                  showFreePointHelp: true,
                  freePointGarmentLabel: 'trouser',
                  selectedPointCount: selectedFreePointsCount,
                })}

                {renderDrawnMeasurementPanel(trouserDrawnRows, 'trouser-drawn-measurements-panel', {
                  cadDense: true,
                  hideSeed: true,
                  hideHelperText: true,
                })}

                <div className="grid grid-cols-2 gap-1.5">
                  {supportsTrouserShorts && (
                    <div className="space-y-1 col-span-2" data-testid="trouser-length-mode-select-wrap">
                      <Label className="text-[8px] font-medium uppercase tracking-[0.16em] text-slate-500">Length</Label>
                      <Select
                        value={trouserLengthMode}
                        onValueChange={(value) => onMeasurementChange('trouserLengthMode', value)}
                        disabled={!canEdit}
                      >
                        <SelectTrigger className={DENSE_SELECT_TRIGGER_CLASS} data-testid="trouser-length-mode-select-trigger">
                          <SelectValue placeholder="Select trouser length" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full" data-testid="trouser-length-mode-option-full">Full</SelectItem>
                          <SelectItem value="short" data-testid="trouser-length-mode-option-short">Shorts</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Front Pattern */}
            <AccordionItem value="front" className={activeTrouserPanel === 'front' ? 'border rounded-lg bg-card px-3' : 'hidden'}>
              <AccordionTrigger className="hidden">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#1e3a8a] text-[10px] text-white flex items-center justify-center font-bold">F</div>
                  <span className="font-semibold text-sm">Front Pattern</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-2 pb-4">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    {renderTrouserInput('creaseOffset', 'Crease Offset', measurements.creaseOffset)}
                    {renderTrouserInput('frontThighSideOffset', 'Side Thigh Offset', measurements.frontThighSideOffset)}
                  </div>

                  <div className="rounded-md border bg-muted/20 p-3 space-y-3" data-testid="front-pleat-controls">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-muted-foreground">Add Pleats</Label>
                      <Checkbox
                        checked={Boolean(measurements.enablePleats)}
                        onCheckedChange={(checked) => onMeasurementChange('enablePleats', checked === true)}
                        disabled={!canEdit}
                        data-testid="front-pleats-enable-checkbox"
                      />
                    </div>

                    {Boolean(measurements.enablePleats) && (
                      <div className="space-y-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs text-muted-foreground">Pleat Count</Label>
                          <Select
                            value={String(measurements.pleatCount || 1)}
                            onValueChange={(value) => onMeasurementChange('pleatCount', Number(value))}
                            disabled={!canEdit}
                          >
                            <SelectTrigger data-testid="front-pleat-count-select">
                              <SelectValue placeholder="Select pleat count" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">1 Pleat</SelectItem>
                              <SelectItem value="2">2 Pleats</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-1.5">
                          <Label className="text-xs text-muted-foreground">Pleat Direction</Label>
                          <Select
                            value={String(measurements.pleatDirection || 'reverse')}
                            onValueChange={(value) => onMeasurementChange('pleatDirection', value)}
                            disabled={!canEdit}
                          >
                            <SelectTrigger data-testid="front-pleat-direction-select">
                              <SelectValue placeholder="Select pleat direction" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="reverse" data-testid="front-pleat-direction-reverse-option">Reverse</SelectItem>
                              <SelectItem value="forward" data-testid="front-pleat-direction-forward-option">Forward</SelectItem>
                              <SelectItem value="both" data-testid="front-pleat-direction-both-option">Both</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <p className="text-[11px] text-muted-foreground" data-testid="front-pleat-direction-note-text">
                          Reverse keeps volume on the side seam, Forward moves it to the center-front / inseam side, and Both splits it evenly. Existing drafts stay Reverse unless you change this.
                        </p>

                        {renderInput('pleatDepth', 'Pleat Depth', measurements.pleatDepth, { commitOnBlurOnly: true })}

                        {Number(measurements.pleatCount) === 2 && (
                          <>
                            {renderInput('secondPleatDepth', '2nd Pleat Depth', measurements.secondPleatDepth, { commitOnBlurOnly: true })}
                            <div data-testid="front-second-pleat-spacing-input">
                              {renderInput('secondPleatSpacing', '2nd Pleat Distance', measurements.secondPleatSpacing, { commitOnBlurOnly: true })}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Back Pattern */}
            <AccordionItem value="back" className={activeTrouserPanel === 'back' ? 'border rounded-lg bg-card px-3' : 'hidden'}>
              <AccordionTrigger className="hidden">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-[#dc2626] text-[10px] text-white flex items-center justify-center font-bold">B</div>
                  <span className="font-semibold text-sm">Back Pattern</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pt-2 pb-4">
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    {renderTrouserInput('cbSeatOffset_X', 'CB Seat X', measurements.cbSeatOffset_X)}
                    {renderTrouserInput('cbSeatOffset_Y', 'CB Seat Y', measurements.cbSeatOffset_Y)}
                    {renderTrouserInput('cbWaistHeight', 'CB Waist Height', measurements.cbWaistHeight)}
                    {renderTrouserInput('cbWaistOffsetX', 'CB Waist X Shift', measurements.cbWaistOffsetX)}
                    {renderTrouserInput('backCreaseOffset', 'Back Crease Offset', measurements.backCreaseOffset)}
                    {renderTrouserInput('backThighSideOffset', 'Side Thigh Offset', measurements.backThighSideOffset)}
                  </div>

                  <div className="rounded-md border bg-muted/20 p-3 space-y-3" data-testid="back-dart-controls">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-muted-foreground">Add Darts</Label>
                      <Checkbox
                        checked={resolvedBackDartMode !== 'none'}
                        onCheckedChange={(checked) => onMeasurementChange('backDartMode', checked === true ? 'one' : 'none')}
                        disabled={!canEdit}
                        data-testid="back-darts-enable-checkbox"
                      />
                    </div>

                    <div className="space-y-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Dart Mode</Label>
                        <Select
                          value={resolvedBackDartMode}
                          onValueChange={(value) => onMeasurementChange('backDartMode', value)}
                          disabled={!canEdit}
                        >
                          <SelectTrigger data-testid="back-dart-mode-select">
                            <SelectValue placeholder="Select dart mode" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="one" data-testid="back-dart-mode-one-option">1 Dart</SelectItem>
                            <SelectItem value="two" data-testid="back-dart-mode-two-option">2 Darts</SelectItem>
                            <SelectItem value="none" data-testid="back-dart-mode-none-option">No Dart</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {resolvedBackDartMode !== 'none' && (
                        <div className="grid grid-cols-2 gap-3">
                          {renderTrouserInput('backDartWidth', 'Dart Width', measurements.backDartWidth)}
                          {renderTrouserInput('backDartDepth', 'Dart Depth', measurements.backDartDepth)}
                        </div>
                      )}

                      {resolvedBackDartMode === 'two' && (
                        <div data-testid="back-two-darts-spacing-input-wrap">
                          {renderTrouserInput('backDartSpacing', 'Dart Spacing', measurements.backDartSpacing)}
                        </div>
                      )}

                      <p className="text-[11px] text-muted-foreground" data-testid="back-dart-mode-help-text">
                        Default is 1 dart. Select 2 darts to split total dart width into two darts, or No Dart to suppress dart drafting.
                      </p>
                    </div>
                  </div>

                  <p className="text-[11px] text-muted-foreground" data-testid="trouser-curve-edit-instruction-text">
                    Curve edit flow: click a trouser curve segment on canvas to open X/Y offset controls in Curve Controls.
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="geometry" className={activeTrouserPanel === 'geometry' ? 'border rounded-lg bg-card px-3' : 'hidden'} data-testid="trouser-geometry-accordion-item">
              <AccordionTrigger className="hidden" />
              <AccordionContent className="pt-2 pb-4 space-y-4" data-testid="trouser-geometry-content">
                <div className="rounded-md border p-3 bg-muted/10 space-y-2" data-testid="trouser-geometry-header-box">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground" data-testid="trouser-geometry-title-text">Trouser Geometry Notes</p>
                  <p className="text-[11px] text-muted-foreground" data-testid="trouser-geometry-description-text">
                    Read-only drafting notes for the current trouser block. These explain how the front and back panels are constructed and how the named geometry points relate to one another.
                  </p>
                </div>

                {isTrouserGeometryV4 && (
                  <div className="rounded-md border border-sky-200 bg-sky-50 p-3 space-y-2" data-testid="trouser-v4-seamwalk-summary-box">
                    <p className="text-xs font-semibold uppercase tracking-wide text-sky-900" data-testid="trouser-v4-seamwalk-title-text">V4 Seam Walk Summary</p>
                    <div className="grid grid-cols-2 gap-2 text-[11px] text-sky-950" data-testid="trouser-v4-seamwalk-grid">
                      <p data-testid="trouser-v4-side-knee-hem-front-text">{isTrouserShortMode ? 'Front side T→H:' : 'Front side K→H:'} <span className="font-semibold">{formatTrouserValue(isTrouserShortMode ? trouserSummary.frontSideThighToHem : trouserSummary.frontSideKneeToHem)}</span>&quot;</p>
                      <p data-testid="trouser-v4-side-knee-hem-back-text">{isTrouserShortMode ? 'Back side T→H:' : 'Back side K→H:'} <span className="font-semibold">{formatTrouserValue(isTrouserShortMode ? trouserSummary.backSideThighToHem : trouserSummary.backSideKneeToHem)}</span>&quot;</p>
                      <p data-testid="trouser-v4-inseam-knee-hem-front-text">{isTrouserShortMode ? 'Front inseam C→H:' : 'Front inseam K→H:'} <span className="font-semibold">{formatTrouserValue(isTrouserShortMode ? trouserSummary.frontInseamCrotchToHem : trouserSummary.frontInseamKneeToHem)}</span>&quot;</p>
                      <p data-testid="trouser-v4-inseam-knee-hem-back-text">{isTrouserShortMode ? 'Back inseam C→H:' : 'Back inseam K→H:'} <span className="font-semibold">{formatTrouserValue(isTrouserShortMode ? trouserSummary.backInseamCrotchToHem : trouserSummary.backInseamKneeToHem)}</span>&quot;</p>
                      <p data-testid="trouser-v4-side-wh-curve-delta-text">Curve Δ W→H: <span className="font-semibold">{formatTrouserValue(trouserSummary.sideWaistToHipCurveDelta)}</span>&quot;</p>
                      <p data-testid="trouser-v4-side-ht-curve-delta-text">Curve Δ H→T: <span className="font-semibold">{formatTrouserValue(trouserSummary.sideHipToThighCurveDelta)}</span>&quot;</p>
                      <p data-testid="trouser-v4-side-tk-curve-delta-text">{isTrouserShortMode ? 'Curve Δ T→H:' : 'Curve Δ T→K:'} <span className="font-semibold">{formatTrouserValue(isTrouserShortMode ? trouserSummary.sideThighToHemCurveDelta : trouserSummary.sideThighToKneeCurveDelta)}</span>&quot;</p>
                      <p data-testid="trouser-v4-inseam-knee-hem-delta-text">{isTrouserShortMode ? 'Inseam Δ C→H:' : 'Inseam Δ K→H:'} <span className="font-semibold">{formatTrouserValue(isTrouserShortMode ? trouserSummary.inseamCrotchToHemDelta : trouserSummary.inseamKneeToHemDelta)}</span>&quot;</p>
                    </div>
                  </div>
                )}

                <Accordion type="single" collapsible defaultValue="trouser-geometry-front" className="space-y-3" data-testid="trouser-geometry-sections-accordion">
                  <AccordionItem value="trouser-geometry-front" className="rounded-md border px-3" data-testid="trouser-geometry-front-accordion-item">
                    <AccordionTrigger className="text-sm font-medium" data-testid="trouser-geometry-front-accordion-trigger">Front Geometry</AccordionTrigger>
                    <AccordionContent className="pt-2 pb-3 space-y-2" data-testid="trouser-geometry-front-accordion-content">
                      {trouserGeometryFrontLines.map((line, index) => (
                        <p
                          key={`trouser-front-geometry-line-${index}`}
                          className="text-[11px] leading-relaxed text-muted-foreground"
                          data-testid={`trouser-geometry-front-line-${index}-text`}
                        >
                          {line}
                        </p>
                      ))}
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="trouser-geometry-back" className="rounded-md border px-3" data-testid="trouser-geometry-back-accordion-item">
                    <AccordionTrigger className="text-sm font-medium" data-testid="trouser-geometry-back-accordion-trigger">Back Geometry</AccordionTrigger>
                    <AccordionContent className="pt-2 pb-3 space-y-2" data-testid="trouser-geometry-back-accordion-content">
                      {trouserGeometryBackLines.map((line, index) => (
                        <p
                          key={`trouser-back-geometry-line-${index}`}
                          className="text-[11px] leading-relaxed text-muted-foreground"
                          data-testid={`trouser-geometry-back-line-${index}-text`}
                        >
                          {line}
                        </p>
                      ))}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
          </div>
        </div>
      </ScrollArea>

      {/* Footer Status */}
      <div className="p-3 border-t bg-muted/10 text-xs text-muted-foreground">
        <div className="flex items-center justify-between">
          <span>Auto-regenerate: <span className="text-green-600 font-semibold">ON</span></span>
          <span>Last update: {new Date().toLocaleTimeString()}</span>
        </div>
      </div>

      {renderTrouserAdvancedPanel()}
    </aside>
  );
}
